/**
 * HexScene - Main Three.js scene with hexagonal grid
 *
 * Features:
 * - Rendered hex grid with merged geometry (performant)
 * - Click to paint hexes with selected color
 * - Hover highlighting
 * - Click pulse ripple animations
 * - Procedural pattern fills
 */

import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { PointerLockControls } from 'three/addons/controls/PointerLockControls.js'
import { HexGrid, HexCoord, HEX_DIRECTIONS } from './HexGrid'
import { soundManager } from './SoundManager'

// Element types with behaviors
export interface HexMaterial {
  id: string
  name: string
  color: number
  emissive: number
  emissiveIntensity: number
  opacity: number
  transparent: boolean
  metalness: number
  roughness: number
  behavior: 'static' | 'flow' | 'fall' | 'spread' | 'rise'
  flowSpeed?: number // ticks between flow steps
  animated?: boolean
}

export const MATERIALS: HexMaterial[] = [
  // Basic colors
  { id: 'cyan', name: 'Cyan', color: 0x4ac8e8, emissive: 0x4ac8e8, emissiveIntensity: 0.15, opacity: 1, transparent: false, metalness: 0.1, roughness: 0.5, behavior: 'static' },
  { id: 'blue', name: 'Blue', color: 0x60a5fa, emissive: 0x60a5fa, emissiveIntensity: 0.15, opacity: 1, transparent: false, metalness: 0.1, roughness: 0.5, behavior: 'static' },
  { id: 'green', name: 'Green', color: 0x4ade80, emissive: 0x4ade80, emissiveIntensity: 0.15, opacity: 1, transparent: false, metalness: 0.1, roughness: 0.5, behavior: 'static' },
  { id: 'purple', name: 'Purple', color: 0xa78bfa, emissive: 0xa78bfa, emissiveIntensity: 0.15, opacity: 1, transparent: false, metalness: 0.1, roughness: 0.5, behavior: 'static' },
  { id: 'pink', name: 'Pink', color: 0xf472b6, emissive: 0xf472b6, emissiveIntensity: 0.15, opacity: 1, transparent: false, metalness: 0.1, roughness: 0.5, behavior: 'static' },

  // Elements
  { id: 'water', name: 'Water', color: 0x3b82f6, emissive: 0x1e40af, emissiveIntensity: 0.1, opacity: 0.7, transparent: true, metalness: 0.3, roughness: 0.1, behavior: 'flow', flowSpeed: 6, animated: true },
  { id: 'lava', name: 'Lava', color: 0xff4500, emissive: 0xff6600, emissiveIntensity: 0.9, opacity: 1, transparent: false, metalness: 0.0, roughness: 0.8, behavior: 'flow', flowSpeed: 15, animated: true },
  { id: 'fire', name: 'Fire', color: 0xff6b35, emissive: 0xff4500, emissiveIntensity: 1.0, opacity: 0.9, transparent: true, metalness: 0.0, roughness: 0.5, behavior: 'spread', flowSpeed: 10, animated: true },
  { id: 'sand', name: 'Sand', color: 0xfbbf24, emissive: 0x000000, emissiveIntensity: 0, opacity: 1, transparent: false, metalness: 0.0, roughness: 0.9, behavior: 'fall' },
  { id: 'stone', name: 'Stone', color: 0x6b7280, emissive: 0x000000, emissiveIntensity: 0, opacity: 1, transparent: false, metalness: 0.2, roughness: 0.9, behavior: 'static' },
  { id: 'earth', name: 'Earth', color: 0x8b5a2b, emissive: 0x000000, emissiveIntensity: 0, opacity: 1, transparent: false, metalness: 0.0, roughness: 1.0, behavior: 'static' },
  { id: 'grass', name: 'Grass', color: 0x22c55e, emissive: 0x166534, emissiveIntensity: 0.05, opacity: 1, transparent: false, metalness: 0.0, roughness: 0.8, behavior: 'static' },
  { id: 'wood', name: 'Wood', color: 0xb45309, emissive: 0x000000, emissiveIntensity: 0, opacity: 1, transparent: false, metalness: 0.0, roughness: 0.85, behavior: 'static' },
  { id: 'glass', name: 'Glass', color: 0xffffff, emissive: 0x88ccff, emissiveIntensity: 0.1, opacity: 0.3, transparent: true, metalness: 0.9, roughness: 0.0, behavior: 'static' },
  { id: 'ice', name: 'Ice', color: 0xa5f3fc, emissive: 0x67e8f9, emissiveIntensity: 0.2, opacity: 0.85, transparent: true, metalness: 0.7, roughness: 0.05, behavior: 'static' },
  { id: 'snow', name: 'Snow', color: 0xf0f9ff, emissive: 0xbae6fd, emissiveIntensity: 0.1, opacity: 1, transparent: false, metalness: 0.0, roughness: 0.6, behavior: 'fall' },
  { id: 'steam', name: 'Steam', color: 0xe0e7ff, emissive: 0xc7d2fe, emissiveIntensity: 0.2, opacity: 0.4, transparent: true, metalness: 0.0, roughness: 0.3, behavior: 'rise', flowSpeed: 4, animated: true },

  // Erase
  { id: 'erase', name: 'Erase', color: 0x1a1a2e, emissive: 0x000000, emissiveIntensity: 0, opacity: 1, transparent: false, metalness: 0, roughness: 1, behavior: 'static' },
]

// Helper to get material by ID
export function getMaterial(id: string): HexMaterial | undefined {
  return MATERIALS.find(m => m.id === id)
}

// Legacy palette for compatibility
export const PALETTE = MATERIALS.map(m => m.color)

interface ClickPulse {
  mesh: THREE.Mesh | THREE.Line
  age: number
  maxAge: number
  type: 'ring' | 'ripple'
  startOpacity?: number
}

interface Particle {
  mesh: THREE.Mesh
  velocity: THREE.Vector3
  age: number
  maxAge: number
  type: 'spark' | 'bubble' | 'steam' | 'ember'
}

// Flammable materials
const FLAMMABLE = ['grass', 'wood']
// Heat sources
const HOT = ['lava', 'fire']
// Cold sources
const COLD = ['ice', 'snow', 'water']

export class HexScene {
  public scene: THREE.Scene
  public camera: THREE.PerspectiveCamera
  public renderer: THREE.WebGLRenderer
  public controls: OrbitControls
  public fpsControls: PointerLockControls | null = null
  public hexGrid: HexGrid

  // Hex data storage: "q,r" -> { material, height }
  private hexData = new Map<string, { material: HexMaterial; height: number }>()

  // Legacy compat
  private hexColors = new Map<string, number>()
  private hexHeights = new Map<string, number>()
  public is3DMode = true // 3D extrusion enabled by default

  // Filled hex meshes
  private hexMeshes = new Map<string, THREE.Mesh>()
  private hexMeshGroup: THREE.Group

  // World grid (outline only)
  private worldHexGrid: THREE.LineSegments | null = null
  private worldFloor: THREE.Mesh | null = null

  // Hover
  private hoverHighlight: THREE.Line | null = null
  private lastHoveredHex: HexCoord | null = null

  // Brush system
  public brushSize = 1 // 1 = single hex, 2 = 7 hexes, 3 = 19 hexes, etc.
  private brushPreviewGroup: THREE.Group | null = null
  private brushPreviewLines: THREE.Line[] = []

  // Drag painting
  private isMouseDown = false
  private paintedThisDrag = new Set<string>() // Track hexes painted in current drag

  // Click pulses
  private clickPulses: ClickPulse[] = []

  // Selected material
  public selectedMaterial: HexMaterial = MATERIALS[0]
  public selectedColor = PALETTE[0] // Legacy compat

  // Symmetry mode
  public symmetryMode: 0 | 2 | 3 | 6 = 0 // 0 = off, 2 = 2-fold, 3 = 3-fold, 6 = 6-fold

  // Game of Life
  private golRunning = false
  private golIntervalId: number | null = null

  // Gravity mode
  private gravityRunning = false
  private gravityIntervalId: number | null = null

  // Element physics simulation
  private physicsRunning = true
  private physicsTick = 0
  private physicsIntervalId: number | null = null

  // Particle system
  private particles: Particle[] = []
  private particleGroup: THREE.Group

  // Dynamic lights from lava/fire
  private dynamicLights: Map<string, THREE.PointLight> = new Map()
  private maxDynamicLights = 20

  // First-person mode
  public isFPSMode = false
  private playerVelocity = new THREE.Vector3()
  private playerOnGround = false
  private moveForward = false
  private moveBackward = false
  private moveLeft = false
  private moveRight = false
  private sprinting = false
  private canJump = true
  public flyMode = true // Toggle between fly and walk mode
  private readonly playerHeight = 2.0 // Eye height
  private readonly playerRadius = 0.4 // Collision radius
  private readonly jumpForce = 0.18
  private readonly gravity = 0.008
  private readonly walkSpeed = 8
  private readonly sprintSpeed = 14
  private readonly flySpeed = 20

  // Tools and mining
  public currentTool: 'hand' | 'pickaxe' | 'shovel' | 'axe' = 'hand'
  private miningTarget: HexCoord | null = null
  private miningProgress = 0
  private miningTime = 0 // Time spent mining current block
  private isMining = false
  public onMiningProgress: ((progress: number, target: HexCoord | null) => void) | null = null
  public onToolChange: ((tool: string) => void) | null = null

  // Callbacks for UI updates
  public onBrushSizeChange: ((size: number) => void) | null = null
  public onSymmetryChange: ((mode: number) => void) | null = null
  public onModeChange: ((isFPS: boolean) => void) | null = null
  public onFlyModeChange: ((isFly: boolean) => void) | null = null

  // Animation
  private container: HTMLElement
  private animationId: number | null = null
  private clock = new THREE.Clock()
  private time = 0

  // Grid config - large grid for expansive art
  private readonly gridRange = 50

  // Sky and atmosphere
  private skyDome: THREE.Mesh | null = null
  private stars: THREE.Points | null = null
  private sun: THREE.Mesh | null = null
  private moon: THREE.Mesh | null = null
  private sunLight: THREE.DirectionalLight | null = null
  private moonLight: THREE.DirectionalLight | null = null
  private dayTime = 0.5 // 0-1, 0.25 = sunrise, 0.5 = noon, 0.75 = sunset, 0 = midnight
  private daySpeed = 0.02 // How fast time passes (0 = paused)
  public autoTime = false // Whether time progresses automatically (off by default for art mode)
  private _skyEnabled = false // Whether sky/fog is visible (enabled by worlds or auto time)

  constructor(container: HTMLElement) {
    this.container = container

    // Scene
    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(0x080c14)

    // Hex grid (radius 1, spacing 1.05 for slight gaps)
    this.hexGrid = new HexGrid(1, 1.05)

    // Camera
    this.camera = new THREE.PerspectiveCamera(
      50,
      container.clientWidth / container.clientHeight,
      0.1,
      1000
    )
    this.camera.position.set(0, 60, 60)
    this.camera.lookAt(0, 0, 0)

    // Renderer
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    })
    this.renderer.setSize(container.clientWidth, container.clientHeight)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    container.appendChild(this.renderer.domElement)

    // Controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.05
    this.controls.maxPolarAngle = Math.PI / 2.1
    this.controls.minDistance = 5
    this.controls.maxDistance = 300
    this.controls.target.set(0, 0, 0)

    // Sky and atmosphere
    this.createSkyDome()
    this.createStars()
    this.createSunMoon()
    this.setupFog()

    // Lighting
    this.setupLighting()

    // Create scene elements
    this.hexMeshGroup = new THREE.Group()
    this.scene.add(this.hexMeshGroup)

    this.particleGroup = new THREE.Group()
    this.scene.add(this.particleGroup)

    this.createWorldFloor()
    this.createWorldHexGrid()
    this.setupHoverHighlight()
    this.setupClickHandler()
    this.setupFPSControls()
    this.startPhysics()

    // Handle resize
    window.addEventListener('resize', this.handleResize)

    // Initialize sky/lighting to current time of day
    this.updateDayNightCycle(0)
  }

  /**
   * Setup first-person controls
   */
  private setupFPSControls(): void {
    this.fpsControls = new PointerLockControls(this.camera, this.renderer.domElement)

    // FPS keyboard controls
    const onKeyDown = (event: KeyboardEvent) => {
      if (!this.isFPSMode) return
      switch (event.code) {
        case 'KeyW': this.moveForward = true; break
        case 'KeyS': this.moveBackward = true; break
        case 'KeyA': this.moveLeft = true; break
        case 'KeyD': this.moveRight = true; break
        case 'Space':
          if (this.flyMode) {
            this.playerVelocity.y = 0.5
          } else if (this.canJump && this.playerOnGround) {
            this.playerVelocity.y = this.jumpForce
            this.canJump = false
            this.playerOnGround = false
          }
          event.preventDefault()
          break
        case 'ShiftLeft':
        case 'ShiftRight':
          if (this.flyMode) {
            this.playerVelocity.y = -0.5
          } else {
            this.sprinting = true
          }
          break
        case 'KeyG': // Toggle fly/walk mode
          this.toggleFlyMode()
          break
        // Tool selection
        case 'Digit1': this.setTool('hand'); break
        case 'Digit2': this.setTool('pickaxe'); break
        case 'Digit3': this.setTool('shovel'); break
        case 'Digit4': this.setTool('axe'); break
      }
    }

    const onKeyUp = (event: KeyboardEvent) => {
      if (!this.isFPSMode) return
      switch (event.code) {
        case 'KeyW': this.moveForward = false; break
        case 'KeyS': this.moveBackward = false; break
        case 'KeyA': this.moveLeft = false; break
        case 'KeyD': this.moveRight = false; break
        case 'Space':
          if (this.flyMode) this.playerVelocity.y = 0
          break
        case 'ShiftLeft':
        case 'ShiftRight':
          if (this.flyMode) {
            this.playerVelocity.y = 0
          } else {
            this.sprinting = false
          }
          break
      }
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('keyup', onKeyUp)

    // Lock/unlock events
    this.fpsControls.addEventListener('lock', () => {
      // Hide brush preview in FPS mode
      if (this.brushPreviewGroup) this.brushPreviewGroup.visible = false
    })

    this.fpsControls.addEventListener('unlock', () => {
      // Stop mining when unlocked
      this.stopMining()
    })

    // FPS mouse controls - left click to mine, right click to place
    this.renderer.domElement.addEventListener('mousedown', (e) => {
      if (!this.isFPSMode || !this.fpsControls?.isLocked) return
      if (e.button === 0) {
        // Left click - start mining
        this.startMining()
      }
    })

    this.renderer.domElement.addEventListener('mouseup', (e) => {
      if (!this.isFPSMode) return
      if (e.button === 0) {
        // Left click release - stop mining
        this.stopMining()
      }
    })

    this.renderer.domElement.addEventListener('contextmenu', (e) => {
      if (!this.isFPSMode || !this.fpsControls?.isLocked) return
      e.preventDefault()
      this.fpsPlaceBlockOnSurface()
    })
  }

  /**
   * Toggle between orbit and FPS mode
   */
  toggleFPSMode(): boolean {
    this.isFPSMode = !this.isFPSMode

    if (this.isFPSMode) {
      // Enter FPS mode
      this.controls.enabled = false
      this.fpsControls?.lock()

      // Position camera at a good starting point if too far
      if (this.camera.position.y > 20) {
        this.camera.position.set(0, 5, 10)
      }
    } else {
      // Exit FPS mode
      this.fpsControls?.unlock()
      this.controls.enabled = true
      this.moveForward = this.moveBackward = this.moveLeft = this.moveRight = false
      this.playerVelocity.set(0, 0, 0)
    }

    this.onModeChange?.(this.isFPSMode)
    return this.isFPSMode
  }

  /**
   * Update FPS movement with proper physics
   */
  private updateFPSMovement(delta: number): void {
    if (!this.isFPSMode || !this.fpsControls?.isLocked) return

    // Get movement direction from camera
    const forward = new THREE.Vector3()
    this.camera.getWorldDirection(forward)

    // Horizontal forward direction
    const horizontalForward = new THREE.Vector3(forward.x, 0, forward.z).normalize()
    const right = new THREE.Vector3()
    right.crossVectors(horizontalForward, new THREE.Vector3(0, 1, 0)).normalize()

    const direction = new THREE.Vector3()

    if (this.flyMode) {
      // Flying: move in camera direction (including up/down)
      if (this.moveForward) direction.add(forward)
      if (this.moveBackward) direction.sub(forward)
      if (this.moveRight) direction.add(right)
      if (this.moveLeft) direction.sub(right)

      if (direction.length() > 0) {
        direction.normalize()
        const speed = this.flySpeed * delta
        this.camera.position.addScaledVector(direction, speed)
      }

      // Fly up/down with velocity
      this.camera.position.y += this.playerVelocity.y * delta * 20
    } else {
      // Walking mode with gravity
      if (this.moveForward) direction.add(horizontalForward)
      if (this.moveBackward) direction.sub(horizontalForward)
      if (this.moveRight) direction.add(right)
      if (this.moveLeft) direction.sub(right)

      // Apply horizontal movement with collision
      if (direction.length() > 0) {
        direction.normalize()
        const speed = (this.sprinting ? this.sprintSpeed : this.walkSpeed) * delta

        // Predict new position
        const newPos = this.camera.position.clone().addScaledVector(direction, speed)

        // Check horizontal collision - sample multiple points around the player
        let canMove = true
        const checkHeight = this.camera.position.y - this.playerHeight + 0.5 // Check at feet level

        for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 3) {
          const checkX = newPos.x + Math.cos(angle) * this.playerRadius
          const checkZ = newPos.z + Math.sin(angle) * this.playerRadius
          const groundAt = this.getGroundHeight(checkX, checkZ)

          // Block movement if there's a wall (ground is too high to step over)
          if (groundAt > checkHeight + 1.2) {
            canMove = false
            break
          }
        }

        if (canMove) {
          this.camera.position.copy(newPos)
        }
      }

      // Apply gravity
      this.playerVelocity.y -= this.gravity
      this.playerVelocity.y = Math.max(this.playerVelocity.y, -0.5) // Terminal velocity
      this.camera.position.y += this.playerVelocity.y

      // Ground collision - check multiple points for stability
      let maxGroundHeight = 0
      for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 4) {
        const checkX = this.camera.position.x + Math.cos(angle) * this.playerRadius * 0.5
        const checkZ = this.camera.position.z + Math.sin(angle) * this.playerRadius * 0.5
        maxGroundHeight = Math.max(maxGroundHeight, this.getGroundHeight(checkX, checkZ))
      }
      maxGroundHeight = Math.max(maxGroundHeight, this.getGroundHeight(this.camera.position.x, this.camera.position.z))

      const targetHeight = maxGroundHeight + this.playerHeight

      if (this.camera.position.y <= targetHeight) {
        this.camera.position.y = targetHeight
        this.playerVelocity.y = 0
        this.playerOnGround = true
        this.canJump = true
      } else {
        this.playerOnGround = false
      }
    }

    // Update mining if holding mouse
    if (this.isMining) {
      this.updateMining(delta)
    }
  }

  /**
   * Toggle between fly mode and walk mode
   */
  toggleFlyMode(): boolean {
    this.flyMode = !this.flyMode
    if (!this.flyMode) {
      // Reset vertical velocity when entering walk mode
      this.playerVelocity.y = 0
    }
    this.onFlyModeChange?.(this.flyMode)
    return this.flyMode
  }

  /**
   * Get ground height at a world position
   */
  private getGroundHeight(x: number, z: number): number {
    const hex = this.hexGrid.cartesianToHex(x, z)
    const key = this.hexGrid.hexKey(hex)
    const data = this.hexData.get(key)
    if (data) {
      return data.height
    }
    return 0
  }

  // Material hardness (time in seconds to break with hand)
  private readonly materialHardness: { [key: string]: number } = {
    cyan: 0.15, blue: 0.15, green: 0.15, purple: 0.15, pink: 0.15,
    water: 0.0, lava: 0.0, fire: 0.0, steam: 0.0, // Instant
    sand: 0.1, earth: 0.15, grass: 0.12,
    stone: 0.4, ice: 0.15, snow: 0.08,
    wood: 0.25, glass: 0.1, erase: 0.0,
  }

  // Tool effectiveness multipliers
  private readonly toolEffectiveness: { [tool: string]: { [material: string]: number } } = {
    hand: {}, // 1x for everything
    pickaxe: { stone: 4, ice: 3, glass: 2 },
    shovel: { sand: 4, earth: 4, grass: 3, snow: 4 },
    axe: { wood: 4, grass: 2 },
  }

  /**
   * Get mining time for a material with current tool
   */
  private getMiningTime(materialId: string): number {
    const baseTime = this.materialHardness[materialId] ?? 1.0
    const toolBonus = this.toolEffectiveness[this.currentTool]?.[materialId] ?? 1
    return baseTime / toolBonus
  }

  /**
   * Set current tool
   */
  setTool(tool: 'hand' | 'pickaxe' | 'shovel' | 'axe'): void {
    this.currentTool = tool
    this.onToolChange?.(tool)
  }

  /**
   * Start mining a block (called on mousedown in FPS mode)
   */
  startMining(): void {
    if (!this.isFPSMode || !this.fpsControls?.isLocked) return

    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(new THREE.Vector2(0, 0), this.camera)
    raycaster.far = 8 // Max reach

    const meshArray = Array.from(this.hexMeshes.values())
    const hits = raycaster.intersectObjects(meshArray)

    if (hits.length > 0) {
      const point = hits[0].point
      const hex = this.hexGrid.cartesianToHex(point.x, point.z)
      const key = this.hexGrid.hexKey(hex)
      const data = this.hexData.get(key)

      if (data) {
        this.isMining = true
        this.miningTarget = hex
        this.miningProgress = 0
        this.miningTime = this.getMiningTime(data.material.id)
      }
    }
  }

  /**
   * Stop mining
   */
  stopMining(): void {
    this.isMining = false
    this.miningTarget = null
    this.miningProgress = 0
    this.onMiningProgress?.(0, null)
  }

  /**
   * Update mining progress
   */
  private updateMining(delta: number): void {
    if (!this.miningTarget) {
      this.stopMining()
      return
    }

    const key = this.hexGrid.hexKey(this.miningTarget)
    const data = this.hexData.get(key)

    if (!data) {
      this.stopMining()
      return
    }

    // Check if still looking at the same block
    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(new THREE.Vector2(0, 0), this.camera)
    raycaster.far = 8

    const meshArray = Array.from(this.hexMeshes.values())
    const hits = raycaster.intersectObjects(meshArray)

    if (hits.length === 0) {
      this.stopMining()
      return
    }

    const hitHex = this.hexGrid.cartesianToHex(hits[0].point.x, hits[0].point.z)
    if (hitHex.q !== this.miningTarget.q || hitHex.r !== this.miningTarget.r) {
      // Started looking at different block, restart
      this.startMining()
      return
    }

    // Progress mining
    this.miningProgress += delta

    // Calculate progress percentage
    const progress = this.miningTime > 0 ? Math.min(this.miningProgress / this.miningTime, 1) : 1
    this.onMiningProgress?.(progress, this.miningTarget)

    // Check if done mining
    if (this.miningProgress >= this.miningTime) {
      // Break the block!
      this.removeHex(this.miningTarget)
      soundManager.play('erase')

      // Spawn particles based on material
      this.spawnParticlesAt(this.miningTarget, 'spark', 5)

      this.stopMining()
    }
  }

  /**
   * Place block in FPS mode (right click)
   */
  fpsPlaceBlockOnSurface(): void {
    if (!this.isFPSMode || !this.fpsControls?.isLocked) return

    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(new THREE.Vector2(0, 0), this.camera)
    raycaster.far = 8

    const meshArray = Array.from(this.hexMeshes.values())
    const hits = raycaster.intersectObjects(meshArray)

    if (hits.length > 0) {
      const hit = hits[0]
      const normal = hit.face?.normal
      const point = hit.point

      // Get the hex we hit
      const hitHex = this.hexGrid.cartesianToHex(point.x, point.z)
      const hitKey = this.hexGrid.hexKey(hitHex)
      const hitData = this.hexData.get(hitKey)

      if (hitData && normal) {
        // Place adjacent block
        if (Math.abs(normal.y) > 0.5) {
          // Top/bottom face - stack on top
          const newHeight = hitData.height + 1
          this.paintHexWithMaterial(hitHex, this.selectedMaterial, newHeight, false)
        } else {
          // Side face - place in adjacent hex
          const worldNormal = normal.clone().applyQuaternion(hit.object.quaternion)
          const adjacentPoint = point.clone().add(worldNormal.multiplyScalar(1.5))
          const adjacentHex = this.hexGrid.cartesianToHex(adjacentPoint.x, adjacentPoint.z)
          if (this.isInRange(adjacentHex)) {
            const adjacentKey = this.hexGrid.hexKey(adjacentHex)
            const adjacentData = this.hexData.get(adjacentKey)
            const newHeight = adjacentData ? adjacentData.height : 1
            this.paintHexWithMaterial(adjacentHex, this.selectedMaterial, newHeight, false)
          }
        }
        soundManager.play('click')
      }
    } else {
      // No block hit, try floor
      if (this.worldFloor) {
        const floorHits = raycaster.intersectObject(this.worldFloor)
        if (floorHits.length > 0) {
          const point = floorHits[0].point
          const hex = this.hexGrid.cartesianToHex(point.x, point.z)
          if (this.isInRange(hex)) {
            this.paintHexWithMaterial(hex, this.selectedMaterial, 1, false)
            soundManager.play('click')
          }
        }
      }
    }
  }

  /**
   * Start element physics simulation
   */
  private startPhysics(): void {
    if (this.physicsIntervalId !== null) return

    this.physicsIntervalId = window.setInterval(() => {
      if (!this.physicsRunning) return
      this.physicsTick++
      this.updateElementPhysics()
    }, 50) // 20 ticks per second
  }

  /**
   * Update flowing/falling elements with interactions
   */
  private updateElementPhysics(): void {
    const movements: { from: string; to: string; material: HexMaterial; height: number }[] = []
    const transformations: { key: string; newMaterial: HexMaterial; spawnParticles?: string }[] = []
    const removals: string[] = []
    const spawns: { hex: HexCoord; material: HexMaterial; height: number }[] = []
    const processed = new Set<string>()

    // Build hex list sorted by r (for gravity-based processing)
    const hexList: { key: string; hex: HexCoord; data: { material: HexMaterial; height: number } }[] = []
    for (const [key, data] of this.hexData) {
      const [q, r] = key.split(',').map(Number)
      hexList.push({ key, hex: { q, r }, data })
    }
    hexList.sort((a, b) => b.hex.r - a.hex.r)

    for (const { key, hex, data } of hexList) {
      if (processed.has(key)) continue
      const { material } = data
      const matId = material.id

      // ========== ELEMENT INTERACTIONS ==========
      for (const dir of HEX_DIRECTIONS) {
        const neighbor = { q: hex.q + dir.q, r: hex.r + dir.r }
        const neighborKey = this.hexGrid.hexKey(neighbor)
        const neighborData = this.hexData.get(neighborKey)
        if (!neighborData) continue

        const neighborId = neighborData.material.id

        // Water + Lava = Stone + Steam
        if ((matId === 'water' && neighborId === 'lava') || (matId === 'lava' && neighborId === 'water')) {
          if (!processed.has(key) && !processed.has(neighborKey)) {
            transformations.push({ key, newMaterial: getMaterial('stone')!, spawnParticles: 'steam' })
            removals.push(neighborKey)
            processed.add(key)
            processed.add(neighborKey)
            // Spawn steam above
            const above = { q: hex.q, r: hex.r - 1 }
            if (this.isInRange(above) && !this.hexData.has(this.hexGrid.hexKey(above))) {
              spawns.push({ hex: above, material: getMaterial('steam')!, height: 0.5 })
            }
          }
        }

        // Lava/Fire melts Ice → Water
        if (HOT.includes(matId) && neighborId === 'ice') {
          if (!processed.has(neighborKey)) {
            transformations.push({ key: neighborKey, newMaterial: getMaterial('water')!, spawnParticles: 'steam' })
            processed.add(neighborKey)
          }
        }

        // Lava/Fire melts Snow → Water (less)
        if (HOT.includes(matId) && neighborId === 'snow') {
          if (!processed.has(neighborKey)) {
            transformations.push({ key: neighborKey, newMaterial: getMaterial('water')! })
            processed.add(neighborKey)
          }
        }

        // Water extinguishes Fire
        if (matId === 'water' && neighborId === 'fire') {
          if (!processed.has(neighborKey)) {
            removals.push(neighborKey)
            processed.add(neighborKey)
            this.spawnParticlesAt(neighbor, 'steam', 3)
          }
        }
      }

      // Skip movement if already processed
      if (processed.has(key)) continue

      // ========== FALLING (Sand, Snow) ==========
      if (material.behavior === 'fall') {
        const below = { q: hex.q, r: hex.r + 1 }
        const belowKey = this.hexGrid.hexKey(below)

        if (this.isInRange(below) && !this.hexData.has(belowKey) && !processed.has(belowKey)) {
          movements.push({ from: key, to: belowKey, material, height: data.height })
          processed.add(key)
          processed.add(belowKey)
        }
      }

      // ========== RISING (Steam) ==========
      if (material.behavior === 'rise' && material.flowSpeed) {
        if (this.physicsTick % material.flowSpeed !== 0) continue

        const above = { q: hex.q, r: hex.r - 1 }
        const aboveKey = this.hexGrid.hexKey(above)

        if (this.isInRange(above) && !this.hexData.has(aboveKey) && !processed.has(aboveKey)) {
          movements.push({ from: key, to: aboveKey, material, height: data.height * 0.9 })
          processed.add(key)
          processed.add(aboveKey)
        } else {
          // Steam dissipates if it can't rise
          if (Math.random() < 0.1) {
            removals.push(key)
            processed.add(key)
            this.spawnParticlesAt(hex, 'steam', 2)
          }
        }
      }

      // ========== FLOWING (Water, Lava) ==========
      if (material.behavior === 'flow' && material.flowSpeed) {
        if (this.physicsTick % material.flowSpeed !== 0) continue

        // Find valid flow targets (prefer downhill)
        const flowTargets: HexCoord[] = []
        for (const dir of HEX_DIRECTIONS) {
          const neighbor = { q: hex.q + dir.q, r: hex.r + dir.r }
          const neighborKey = this.hexGrid.hexKey(neighbor)
          if (this.isInRange(neighbor) && !this.hexData.has(neighborKey) && !processed.has(neighborKey)) {
            flowTargets.push(neighbor)
          }
        }

        // Sort by r (prefer downhill)
        flowTargets.sort((a, b) => b.r - a.r)

        if (flowTargets.length > 0 && data.height > 0.2) {
          const target = flowTargets[0]
          const newKey = this.hexGrid.hexKey(target)
          spawns.push({ hex: target, material, height: 0.2 })
          data.height -= 0.1
          this.hexData.set(key, data)
          this.updateHexMesh(hex, data)
          processed.add(newKey)

          // Spawn particles
          if (matId === 'lava' && Math.random() < 0.3) {
            this.spawnParticlesAt(hex, 'ember', 1)
          }
        }
      }

      // ========== SPREADING (Fire) ==========
      if (material.behavior === 'spread' && material.flowSpeed) {
        if (this.physicsTick % material.flowSpeed !== 0) continue

        // Fire spreads to flammable neighbors
        for (const dir of HEX_DIRECTIONS) {
          const neighbor = { q: hex.q + dir.q, r: hex.r + dir.r }
          const neighborKey = this.hexGrid.hexKey(neighbor)
          const neighborData = this.hexData.get(neighborKey)

          if (neighborData && FLAMMABLE.includes(neighborData.material.id) && !processed.has(neighborKey)) {
            if (Math.random() < 0.3) {
              transformations.push({ key: neighborKey, newMaterial: getMaterial('fire')! })
              processed.add(neighborKey)
              this.spawnParticlesAt(neighbor, 'ember', 2)
            }
          }
        }

        // Fire burns out eventually
        if (Math.random() < 0.05) {
          removals.push(key)
          processed.add(key)
          this.spawnParticlesAt(hex, 'ember', 3)
        }
      }
    }

    // ========== APPLY ALL CHANGES ==========

    // Removals first
    for (const key of removals) {
      const [q, r] = key.split(',').map(Number)
      this.removeHex({ q, r })
    }

    // Transformations
    for (const { key, newMaterial, spawnParticles } of transformations) {
      const [q, r] = key.split(',').map(Number)
      const hex = { q, r }
      const oldData = this.hexData.get(key)
      this.removeHex(hex)
      this.paintHexWithMaterial(hex, newMaterial, oldData?.height || 0.3, true)
      if (spawnParticles) {
        this.spawnParticlesAt(hex, spawnParticles as any, 5)
      }
    }

    // Movements
    for (const { from, to, material, height } of movements) {
      const [fq, fr] = from.split(',').map(Number)
      const [tq, tr] = to.split(',').map(Number)
      this.removeHex({ q: fq, r: fr })
      this.paintHexWithMaterial({ q: tq, r: tr }, material, height, true)
    }

    // Spawns
    for (const { hex, material, height } of spawns) {
      if (!this.hexData.has(this.hexGrid.hexKey(hex))) {
        this.paintHexWithMaterial(hex, material, height, true)
      }
    }

    // Update dynamic lights
    this.updateDynamicLights()
  }

  /**
   * Spawn particles at a hex position
   */
  private spawnParticlesAt(hex: HexCoord, type: 'spark' | 'bubble' | 'steam' | 'ember', count: number): void {
    const { x, z } = this.hexGrid.axialToCartesian(hex)
    const data = this.hexData.get(this.hexGrid.hexKey(hex))
    const baseY = data ? data.height : 0.5

    for (let i = 0; i < count; i++) {
      const geometry = new THREE.SphereGeometry(0.05 + Math.random() * 0.05, 6, 6)
      let color: number
      let emissive: number

      switch (type) {
        case 'spark':
          color = 0xffff00
          emissive = 0xffaa00
          break
        case 'ember':
          color = 0xff4400
          emissive = 0xff2200
          break
        case 'steam':
          color = 0xffffff
          emissive = 0xcccccc
          break
        case 'bubble':
          color = 0x88ccff
          emissive = 0x4488ff
          break
      }

      const material = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.8,
      })

      const mesh = new THREE.Mesh(geometry, material)
      mesh.position.set(
        x + (Math.random() - 0.5) * 0.5,
        baseY + Math.random() * 0.5,
        z + (Math.random() - 0.5) * 0.5
      )

      this.particleGroup.add(mesh)

      const velocityY = type === 'steam' || type === 'spark' ? 0.05 + Math.random() * 0.05 : 0.02
      const velocityXZ = (Math.random() - 0.5) * 0.02

      this.particles.push({
        mesh,
        velocity: new THREE.Vector3(velocityXZ, velocityY, velocityXZ),
        age: 0,
        maxAge: 0.5 + Math.random() * 0.5,
        type,
      })
    }
  }

  /**
   * Update particles
   */
  private updateParticles(delta: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]
      p.age += delta

      if (p.age >= p.maxAge) {
        this.particleGroup.remove(p.mesh)
        p.mesh.geometry.dispose()
        ;(p.mesh.material as THREE.Material).dispose()
        this.particles.splice(i, 1)
        continue
      }

      // Update position
      p.mesh.position.add(p.velocity)

      // Fade out
      const fade = 1 - (p.age / p.maxAge)
      ;(p.mesh.material as THREE.MeshBasicMaterial).opacity = fade * 0.8

      // Slow down
      p.velocity.multiplyScalar(0.98)
    }
  }

  /**
   * Update dynamic lights from hot elements
   */
  private updateDynamicLights(): void {
    const hotHexes: { key: string; hex: HexCoord; intensity: number }[] = []

    for (const [key, data] of this.hexData) {
      if (HOT.includes(data.material.id)) {
        const [q, r] = key.split(',').map(Number)
        const intensity = data.material.id === 'lava' ? 1.5 : 1.0
        hotHexes.push({ key, hex: { q, r }, intensity })
      }
    }

    // Remove lights for hexes that are no longer hot
    for (const [key, light] of this.dynamicLights) {
      if (!hotHexes.find(h => h.key === key)) {
        this.scene.remove(light)
        this.dynamicLights.delete(key)
      }
    }

    // Add/update lights (limit total)
    let lightCount = 0
    for (const { key, hex, intensity } of hotHexes) {
      if (lightCount >= this.maxDynamicLights) break

      const { x, z } = this.hexGrid.axialToCartesian(hex)
      const data = this.hexData.get(key)
      const y = (data?.height || 0.5) + 0.5

      let light = this.dynamicLights.get(key)
      if (!light) {
        light = new THREE.PointLight(0xff6600, intensity, 8, 2)
        this.scene.add(light)
        this.dynamicLights.set(key, light)
      }

      light.position.set(x, y, z)
      // Flicker effect
      light.intensity = intensity * (0.8 + Math.random() * 0.4)
      lightCount++
    }
  }

  /**
   * Check if hex is in range
   */
  private isInRange(hex: HexCoord): boolean {
    return Math.abs(hex.q) + Math.abs(hex.r) + Math.abs(-hex.q - hex.r) <= this.gridRange * 2
  }

  /**
   * Remove a hex
   */
  private removeHex(hex: HexCoord): void {
    const key = this.hexGrid.hexKey(hex)
    this.hexData.delete(key)
    this.hexColors.delete(key)
    this.hexHeights.delete(key)

    const mesh = this.hexMeshes.get(key)
    if (mesh) {
      this.hexMeshGroup.remove(mesh)
      mesh.geometry.dispose()
      ;(mesh.material as THREE.Material).dispose()
      this.hexMeshes.delete(key)
    }
  }

  /**
   * Update mesh for existing hex
   */
  private updateHexMesh(hex: HexCoord, data: { material: HexMaterial; height: number }): void {
    const key = this.hexGrid.hexKey(hex)
    const { x, z } = this.hexGrid.axialToCartesian(hex)

    // Remove old mesh
    const oldMesh = this.hexMeshes.get(key)
    if (oldMesh) {
      this.hexMeshGroup.remove(oldMesh)
      oldMesh.geometry.dispose()
      ;(oldMesh.material as THREE.Material).dispose()
    }

    // Create new mesh
    const shape = this.createHexShape(this.hexGrid.hexRadius * 0.95)
    let geometry: THREE.BufferGeometry

    if (this.is3DMode && data.height > 0) {
      geometry = new THREE.ExtrudeGeometry(shape, {
        depth: data.height,
        bevelEnabled: false,
      })
    } else {
      geometry = new THREE.ShapeGeometry(shape)
    }

    const mat = data.material
    const material = new THREE.MeshStandardMaterial({
      color: mat.color,
      emissive: mat.emissive,
      emissiveIntensity: mat.emissiveIntensity,
      roughness: mat.roughness,
      metalness: mat.metalness,
      transparent: mat.transparent,
      opacity: mat.opacity,
    })

    const mesh = new THREE.Mesh(geometry, material)
    mesh.rotation.x = -Math.PI / 2
    mesh.position.set(x, 0.02, z)

    this.hexMeshGroup.add(mesh)
    this.hexMeshes.set(key, mesh)
  }

  /**
   * Paint hex with specific material
   */
  paintHexWithMaterial(hex: HexCoord, material: HexMaterial, height: number = 0.3, silent = false): void {
    const key = this.hexGrid.hexKey(hex)

    // Handle erase
    if (material.id === 'erase') {
      this.removeHex(hex)
      if (!silent) soundManager.play('erase')
      return
    }

    // Get current data and add height
    const current = this.hexData.get(key)
    const newHeight = (current?.height || 0) + height

    // Store data
    this.hexData.set(key, { material, height: newHeight })
    this.hexColors.set(key, material.color)
    this.hexHeights.set(key, newHeight)

    // Update mesh
    this.updateHexMesh(hex, { material, height: newHeight })
  }

  /**
   * Create gradient sky dome
   */
  private createSkyDome(): void {
    const geometry = new THREE.SphereGeometry(400, 32, 32)

    // Custom shader for gradient sky
    const material = new THREE.ShaderMaterial({
      uniforms: {
        topColor: { value: new THREE.Color(0x0077ff) },
        bottomColor: { value: new THREE.Color(0x89cff0) },
        horizonColor: { value: new THREE.Color(0xffffff) },
        sunPosition: { value: new THREE.Vector3(0, 1, 0) },
        sunColor: { value: new THREE.Color(0xffffee) },
        nightFactor: { value: 0.0 },
      },
      vertexShader: `
        varying vec3 vWorldPosition;
        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPosition = worldPosition.xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 topColor;
        uniform vec3 bottomColor;
        uniform vec3 horizonColor;
        uniform vec3 sunPosition;
        uniform vec3 sunColor;
        uniform float nightFactor;
        varying vec3 vWorldPosition;

        void main() {
          float h = normalize(vWorldPosition).y;

          // Sky gradient
          vec3 skyColor;
          if (h > 0.0) {
            skyColor = mix(horizonColor, topColor, pow(h, 0.4));
          } else {
            skyColor = mix(horizonColor, bottomColor, pow(-h, 0.4));
          }

          // Sun glow
          vec3 sunDir = normalize(sunPosition);
          float sunDot = max(dot(normalize(vWorldPosition), sunDir), 0.0);
          float sunGlow = pow(sunDot, 32.0) * 0.5 + pow(sunDot, 256.0) * 1.0;
          skyColor += sunColor * sunGlow * (1.0 - nightFactor);

          // Night darkening
          vec3 nightColor = vec3(0.02, 0.02, 0.08);
          skyColor = mix(skyColor, nightColor, nightFactor);

          gl_FragColor = vec4(skyColor, 1.0);
        }
      `,
      side: THREE.BackSide,
    })

    this.skyDome = new THREE.Mesh(geometry, material)
    this.scene.add(this.skyDome)
  }

  /**
   * Create starfield for night sky
   */
  private createStars(): void {
    const starsGeometry = new THREE.BufferGeometry()
    const starCount = 2000
    const positions = new Float32Array(starCount * 3)
    const sizes = new Float32Array(starCount)

    for (let i = 0; i < starCount; i++) {
      // Random position on sphere
      const theta = Math.random() * Math.PI * 2
      const phi = Math.acos(2 * Math.random() - 1)
      const radius = 380

      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta)
      positions[i * 3 + 1] = radius * Math.cos(phi)
      positions[i * 3 + 2] = radius * Math.sin(phi) * Math.sin(theta)

      sizes[i] = 0.5 + Math.random() * 1.5
    }

    starsGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    starsGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1))

    const starsMaterial = new THREE.ShaderMaterial({
      uniforms: {
        opacity: { value: 0.0 },
        time: { value: 0.0 },
      },
      vertexShader: `
        attribute float size;
        uniform float time;
        varying float vBrightness;
        void main() {
          vBrightness = 0.5 + 0.5 * sin(time * 2.0 + position.x * 0.01);
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (300.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform float opacity;
        varying float vBrightness;
        void main() {
          float r = distance(gl_PointCoord, vec2(0.5));
          if (r > 0.5) discard;
          float alpha = (1.0 - r * 2.0) * opacity * vBrightness;
          gl_FragColor = vec4(1.0, 1.0, 1.0, alpha);
        }
      `,
      transparent: true,
      depthWrite: false,
    })

    this.stars = new THREE.Points(starsGeometry, starsMaterial)
    this.scene.add(this.stars)
  }

  /**
   * Create sun and moon
   */
  private createSunMoon(): void {
    // Sun
    const sunGeometry = new THREE.SphereGeometry(15, 32, 32)
    const sunMaterial = new THREE.MeshBasicMaterial({
      color: 0xffff88,
      transparent: true,
    })
    this.sun = new THREE.Mesh(sunGeometry, sunMaterial)
    this.scene.add(this.sun)

    // Sun light
    this.sunLight = new THREE.DirectionalLight(0xffffee, 1.0)
    this.sunLight.castShadow = true
    this.scene.add(this.sunLight)

    // Moon
    const moonGeometry = new THREE.SphereGeometry(8, 32, 32)
    const moonMaterial = new THREE.MeshBasicMaterial({
      color: 0xeeeeff,
      transparent: true,
    })
    this.moon = new THREE.Mesh(moonGeometry, moonMaterial)
    this.scene.add(this.moon)

    // Moon light (dimmer, blue-ish)
    this.moonLight = new THREE.DirectionalLight(0x8888ff, 0.2)
    this.scene.add(this.moonLight)
  }

  /**
   * Setup fog for atmosphere
   */
  private setupFog(): void {
    this.scene.fog = new THREE.FogExp2(0x89cff0, 0.003)
  }

  /**
   * Update sky, sun, moon, stars based on time of day
   */
  private updateDayNightCycle(delta: number): void {
    if (this.autoTime) {
      this.dayTime = (this.dayTime + delta * this.daySpeed) % 1.0
    }

    const t = this.dayTime
    const sunAngle = t * Math.PI * 2 - Math.PI / 2 // -90° at midnight, 90° at noon
    const sunHeight = Math.sin(sunAngle)
    const sunX = Math.cos(sunAngle) * 300
    const sunY = sunHeight * 300

    // Position sun
    if (this.sun) {
      this.sun.position.set(sunX, sunY, -100)
      this.sun.visible = sunHeight > -0.2
    }

    // Position sun light
    if (this.sunLight) {
      this.sunLight.position.set(sunX * 0.5, sunY * 0.5 + 50, -50)
      const sunIntensity = Math.max(0, sunHeight) * 1.2
      this.sunLight.intensity = sunIntensity
    }

    // Position moon (opposite side)
    if (this.moon) {
      this.moon.position.set(-sunX, -sunY, -100)
      this.moon.visible = sunHeight < 0.2
    }

    // Moon light
    if (this.moonLight) {
      this.moonLight.position.set(-sunX * 0.5, -sunY * 0.5 + 50, -50)
      const moonIntensity = Math.max(0, -sunHeight) * 0.3
      this.moonLight.intensity = moonIntensity
    }

    // Night factor (0 = day, 1 = night)
    const nightFactor = Math.max(0, Math.min(1, -sunHeight * 2 + 0.3))

    // Update sky colors
    if (this.skyDome) {
      const mat = this.skyDome.material as THREE.ShaderMaterial

      // Daytime colors
      const dayTop = new THREE.Color(0x0077ff)
      const dayHorizon = new THREE.Color(0x89cff0)
      const dayBottom = new THREE.Color(0x4ac8e8)

      // Sunset colors
      const sunsetTop = new THREE.Color(0x1a0533)
      const sunsetHorizon = new THREE.Color(0xff6b35)
      const sunsetBottom = new THREE.Color(0x2d1b4e)

      // Night colors
      const nightTop = new THREE.Color(0x000011)
      const nightHorizon = new THREE.Color(0x0a0a20)
      const nightBottom = new THREE.Color(0x050510)

      // Calculate blend based on sun position
      let topColor, horizonColor, bottomColor

      if (sunHeight > 0.1) {
        // Day
        topColor = dayTop
        horizonColor = dayHorizon
        bottomColor = dayBottom
      } else if (sunHeight > -0.1) {
        // Sunrise/sunset
        const blend = (sunHeight + 0.1) / 0.2
        topColor = new THREE.Color().lerpColors(sunsetTop, dayTop, blend)
        horizonColor = new THREE.Color().lerpColors(sunsetHorizon, dayHorizon, blend)
        bottomColor = new THREE.Color().lerpColors(sunsetBottom, dayBottom, blend)
      } else if (sunHeight > -0.3) {
        // Twilight
        const blend = (sunHeight + 0.3) / 0.2
        topColor = new THREE.Color().lerpColors(nightTop, sunsetTop, blend)
        horizonColor = new THREE.Color().lerpColors(nightHorizon, sunsetHorizon, blend)
        bottomColor = new THREE.Color().lerpColors(nightBottom, sunsetBottom, blend)
      } else {
        // Night
        topColor = nightTop
        horizonColor = nightHorizon
        bottomColor = nightBottom
      }

      mat.uniforms.topColor.value = topColor
      mat.uniforms.horizonColor.value = horizonColor
      mat.uniforms.bottomColor.value = bottomColor
      mat.uniforms.sunPosition.value.set(sunX, sunY, -100).normalize()
      mat.uniforms.nightFactor.value = nightFactor
    }

    // Update stars visibility
    if (this.stars) {
      const starMat = this.stars.material as THREE.ShaderMaterial
      starMat.uniforms.opacity.value = nightFactor
      starMat.uniforms.time.value = this.time
    }

    // Only show sky/fog when time features are active
    if (this.autoTime || this._skyEnabled) {
      // Update fog color
      if (this.scene.fog) {
        const fog = this.scene.fog as THREE.FogExp2
        const dayFog = new THREE.Color(0x89cff0)
        const nightFog = new THREE.Color(0x0a0a20)
        fog.color.lerpColors(nightFog, dayFog, 1 - nightFactor)
        fog.density = 0.003
      }
      // Let sky dome handle background
      this.scene.background = null
      if (this.skyDome) this.skyDome.visible = true
      if (this.stars) this.stars.visible = true
      if (this.sun) this.sun.visible = true
      if (this.moon) this.moon.visible = true
    } else {
      // Art mode: dark background, no sky/fog
      this.scene.background = new THREE.Color(0x080c14)
      if (this.scene.fog) {
        (this.scene.fog as THREE.FogExp2).density = 0
      }
      if (this.skyDome) this.skyDome.visible = false
      if (this.stars) this.stars.visible = false
      if (this.sun) this.sun.visible = false
      if (this.moon) this.moon.visible = false
    }
  }

  /**
   * Set time of day (0-1, 0.5 = noon, 0 = midnight)
   */
  setTimeOfDay(time: number): void {
    this.dayTime = time % 1.0
  }

  /**
   * Get current time of day
   */
  getTimeOfDay(): number {
    return this.dayTime
  }

  private setupLighting(): void {
    // Ambient light (will be modulated by day/night)
    const ambient = new THREE.AmbientLight(0xffffff, 0.4)
    this.scene.add(ambient)

    // Hemisphere for subtle color variation
    const hemi = new THREE.HemisphereLight(0x87ceeb, 0x3d2817, 0.4)
    this.scene.add(hemi)
  }

  /**
   * Invisible floor for raycasting clicks
   */
  private createWorldFloor(): void {
    const geometry = new THREE.PlaneGeometry(500, 500)
    const material = new THREE.MeshBasicMaterial({
      visible: false,
    })
    const floor = new THREE.Mesh(geometry, material)
    floor.rotation.x = -Math.PI / 2
    floor.position.y = -0.05
    this.scene.add(floor)
    this.worldFloor = floor
  }

  /**
   * Create world hex grid using merged geometry for performance
   */
  private createWorldHexGrid(): void {
    const hexRadius = this.hexGrid.hexRadius
    const vertices: number[] = []

    // Precompute hex corner angles (pointy-top)
    const angles: number[] = []
    for (let i = 0; i < 6; i++) {
      angles.push((Math.PI / 3) * i - Math.PI / 2)
    }

    // Generate hex outlines
    for (let q = -this.gridRange; q <= this.gridRange; q++) {
      for (let r = -this.gridRange; r <= this.gridRange; r++) {
        // Keep roughly circular
        if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) > this.gridRange * 2) continue

        const { x, z } = this.hexGrid.axialToCartesian({ q, r })

        // 6 line segments per hex
        for (let i = 0; i < 6; i++) {
          const startAngle = angles[i]
          const endAngle = angles[(i + 1) % 6]

          vertices.push(
            x + hexRadius * Math.cos(startAngle), 0, z + hexRadius * Math.sin(startAngle),
            x + hexRadius * Math.cos(endAngle), 0, z + hexRadius * Math.sin(endAngle)
          )
        }
      }
    }

    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3))

    const material = new THREE.LineBasicMaterial({
      color: 0x4ac8e8,
      transparent: true,
      opacity: 0.25,
    })

    const lines = new THREE.LineSegments(geometry, material)
    lines.position.y = 0.01
    this.scene.add(lines)
    this.worldHexGrid = lines
  }

  /**
   * Setup hover highlight and brush preview
   */
  private setupHoverHighlight(): void {
    // Create brush preview group
    this.brushPreviewGroup = new THREE.Group()
    this.scene.add(this.brushPreviewGroup)

    // Create initial single hex highlight
    this.updateBrushPreviewGeometry()

    this.renderer.domElement.addEventListener('mousemove', this.handleHover)
    this.renderer.domElement.addEventListener('mouseleave', this.handleHoverLeave)
  }

  /**
   * Create hex outline geometry for brush preview
   */
  private createHexOutlineGeometry(): THREE.BufferGeometry {
    const hexRadius = this.hexGrid.hexRadius
    const points: THREE.Vector3[] = []
    for (let i = 0; i <= 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 2
      points.push(new THREE.Vector3(
        hexRadius * Math.cos(angle),
        0.03,
        hexRadius * Math.sin(angle)
      ))
    }
    return new THREE.BufferGeometry().setFromPoints(points)
  }

  /**
   * Update brush preview geometry based on current brush size
   */
  private updateBrushPreviewGeometry(): void {
    if (!this.brushPreviewGroup) return

    // Clear existing preview lines
    for (const line of this.brushPreviewLines) {
      this.brushPreviewGroup.remove(line)
      line.geometry.dispose()
      ;(line.material as THREE.Material).dispose()
    }
    this.brushPreviewLines = []

    // Create lines for each hex in brush radius
    const brushRadius = this.brushSize - 1
    const hexes = this.hexGrid.getHexesInRadius({ q: 0, r: 0 }, brushRadius)

    for (const hex of hexes) {
      const geometry = this.createHexOutlineGeometry()
      const material = new THREE.LineBasicMaterial({
        color: 0x8eefff,
        transparent: true,
        opacity: 0.7,
      })
      const line = new THREE.Line(geometry, material)

      // Position relative to brush center
      const { x, z } = this.hexGrid.axialToCartesian(hex)
      line.position.set(x, 0, z)

      this.brushPreviewGroup.add(line)
      this.brushPreviewLines.push(line)
    }

    this.brushPreviewGroup.visible = false
  }

  /**
   * Set brush size and update preview
   */
  setBrushSize(size: number): void {
    const newSize = Math.max(1, Math.min(10, size)) // Clamp 1-10
    if (newSize !== this.brushSize) {
      this.brushSize = newSize
      this.updateBrushPreviewGeometry()
      this.onBrushSizeChange?.(this.brushSize)
    }
  }

  /**
   * Cycle through symmetry modes: 0 → 2 → 3 → 6 → 0
   */
  cycleSymmetry(): void {
    const modes: (0 | 2 | 3 | 6)[] = [0, 2, 3, 6]
    const currentIndex = modes.indexOf(this.symmetryMode)
    this.symmetryMode = modes[(currentIndex + 1) % modes.length]
    this.onSymmetryChange?.(this.symmetryMode)
  }

  /**
   * Set specific symmetry mode
   */
  setSymmetry(mode: 0 | 2 | 3 | 6): void {
    this.symmetryMode = mode
    this.onSymmetryChange?.(this.symmetryMode)
  }

  /**
   * Get all symmetric positions for a hex coordinate
   * Rotates around origin (0,0)
   */
  private getSymmetricHexes(hex: HexCoord): HexCoord[] {
    if (this.symmetryMode === 0) return [hex]

    const { q, r } = hex
    const positions: HexCoord[] = [hex]

    // 6-fold symmetry rotations (60° increments in axial coordinates)
    // Rotation by 60° clockwise: (q, r) → (-r, q+r)
    const rotations: HexCoord[] = [
      { q, r },                    // 0°
      { q: -r, r: q + r },         // 60°
      { q: -q - r, r: q },         // 120°
      { q: -q, r: -r },            // 180°
      { q: r, r: -q - r },         // 240°
      { q: q + r, r: -q },         // 300°
    ]

    if (this.symmetryMode === 6) {
      return rotations
    } else if (this.symmetryMode === 3) {
      // 3-fold: every other rotation (0°, 120°, 240°)
      return [rotations[0], rotations[2], rotations[4]]
    } else if (this.symmetryMode === 2) {
      // 2-fold: opposite points (0°, 180°)
      return [rotations[0], rotations[3]]
    }

    return positions
  }

  private handleHover = (event: MouseEvent): void => {
    if (!this.brushPreviewGroup || !this.worldFloor) return

    const rect = this.renderer.domElement.getBoundingClientRect()
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    )

    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(mouse, this.camera)
    const intersects = raycaster.intersectObject(this.worldFloor)

    if (intersects.length > 0) {
      const point = intersects[0].point
      const hexCoord = this.hexGrid.cartesianToHex(point.x, point.z)

      // Check if in grid range
      if (Math.abs(hexCoord.q) + Math.abs(hexCoord.r) + Math.abs(-hexCoord.q - hexCoord.r) <= this.gridRange * 2) {
        const hexCenter = this.hexGrid.axialToCartesian(hexCoord)

        // Position brush preview at hex center
        this.brushPreviewGroup.position.set(hexCenter.x, 0, hexCenter.z)
        this.brushPreviewGroup.visible = true

        // Play hover sound if moved to a new hex
        const isNewHex = !this.lastHoveredHex ||
          this.lastHoveredHex.q !== hexCoord.q ||
          this.lastHoveredHex.r !== hexCoord.r

        if (isNewHex) {
          // Calculate distance from center for pitch
          const dist = this.hexGrid.distance(hexCoord, { q: 0, r: 0 })
          const normalizedDist = dist / this.gridRange
          soundManager.playHover(normalizedDist)

          // If dragging, paint this hex
          if (this.isMouseDown) {
            this.paintAtHex(hexCoord, point.x, point.z)
          }
        }

        this.lastHoveredHex = hexCoord
      } else {
        this.brushPreviewGroup.visible = false
        this.lastHoveredHex = null
      }
    } else {
      this.brushPreviewGroup.visible = false
      this.lastHoveredHex = null
    }
  }

  private handleHoverLeave = (): void => {
    if (this.brushPreviewGroup) {
      this.brushPreviewGroup.visible = false
    }
    this.lastHoveredHex = null
    this.isMouseDown = false
    this.paintedThisDrag.clear()
  }

  /**
   * Paint at a hex position (used by both click and drag)
   */
  private paintAtHex(hexCoord: HexCoord, worldX: number, worldZ: number): void {
    const brushRadius = this.brushSize - 1
    const hexesToPaint = this.hexGrid.getHexesInRadius(hexCoord, brushRadius)

    let paintedAny = false

    for (const hex of hexesToPaint) {
      // Get all symmetric positions for this hex
      const symmetricHexes = this.getSymmetricHexes(hex)

      for (const symHex of symmetricHexes) {
        // Check if in grid range
        if (!this.isInRange(symHex)) continue

        const key = this.hexGrid.hexKey(symHex)

        // Skip if already painted in this drag
        if (this.paintedThisDrag.has(key)) continue

        this.paintedThisDrag.add(key)
        this.paintHexWithMaterial(symHex, this.selectedMaterial, 0.3, true)
        paintedAny = true
      }
    }

    if (paintedAny) {
      // Only spawn pulse and sound once per paint action
      this.spawnClickPulse(worldX, worldZ)
      soundManager.play('click')
    }
  }

  /**
   * Setup click and drag handler
   */
  private setupClickHandler(): void {
    // Mouse down - start potential drag
    this.renderer.domElement.addEventListener('mousedown', (event) => {
      if (event.button !== 0) return // Left click only

      this.isMouseDown = true
      this.paintedThisDrag.clear()

      // Handle click at current position
      if (this.lastHoveredHex && this.worldFloor) {
        const rect = this.renderer.domElement.getBoundingClientRect()
        const mouse = new THREE.Vector2(
          ((event.clientX - rect.left) / rect.width) * 2 - 1,
          -((event.clientY - rect.top) / rect.height) * 2 + 1
        )

        const raycaster = new THREE.Raycaster()
        raycaster.setFromCamera(mouse, this.camera)
        const intersects = raycaster.intersectObject(this.worldFloor)

        if (intersects.length > 0) {
          const point = intersects[0].point
          this.paintAtHex(this.lastHoveredHex, point.x, point.z)
        }
      }
    })

    // Mouse up - end drag
    this.renderer.domElement.addEventListener('mouseup', () => {
      this.isMouseDown = false
      this.paintedThisDrag.clear()
    })

    // Also handle mouse leaving window
    window.addEventListener('mouseup', () => {
      this.isMouseDown = false
      this.paintedThisDrag.clear()
    })
  }

  /**
   * Paint a hex with a color
   */
  paintHex(hex: HexCoord, color: number, silent = false): void {
    const key = this.hexGrid.hexKey(hex)
    const { x, z } = this.hexGrid.axialToCartesian(hex)

    // If color is the "erase" color, remove the hex
    if (color === 0x1a1a2e) {
      this.hexColors.delete(key)
      this.hexHeights.delete(key)
      const mesh = this.hexMeshes.get(key)
      if (mesh) {
        this.hexMeshGroup.remove(mesh)
        mesh.geometry.dispose()
        ;(mesh.material as THREE.Material).dispose()
        this.hexMeshes.delete(key)
        if (!silent) soundManager.play('erase')
      }
      return
    }

    // Get current height and increase it
    const currentHeight = this.hexHeights.get(key) || 0
    const newHeight = currentHeight + 0.3
    this.hexHeights.set(key, newHeight)
    this.hexColors.set(key, color)

    // Remove old mesh if exists
    const oldMesh = this.hexMeshes.get(key)
    if (oldMesh) {
      this.hexMeshGroup.remove(oldMesh)
      oldMesh.geometry.dispose()
      ;(oldMesh.material as THREE.Material).dispose()
    }

    // Create new mesh (extruded in 3D mode, flat otherwise)
    const shape = this.createHexShape(this.hexGrid.hexRadius * 0.95)
    let geometry: THREE.BufferGeometry

    if (this.is3DMode && newHeight > 0) {
      geometry = new THREE.ExtrudeGeometry(shape, {
        depth: newHeight,
        bevelEnabled: false,
      })
      // ExtrudeGeometry creates shape in XY plane, we need to rotate
    } else {
      geometry = new THREE.ShapeGeometry(shape)
    }

    const material = new THREE.MeshStandardMaterial({
      color: color,
      emissive: color,
      emissiveIntensity: 0.15,
      roughness: 0.5,
      metalness: 0.1,
    })

    const mesh = new THREE.Mesh(geometry, material)

    if (this.is3DMode && newHeight > 0) {
      // Extruded: rotate and position so it stands up
      mesh.rotation.x = -Math.PI / 2
      mesh.position.set(x, 0.02, z)
    } else {
      // Flat
      mesh.rotation.x = -Math.PI / 2
      mesh.position.set(x, 0.02, z)
    }

    this.hexMeshGroup.add(mesh)
    this.hexMeshes.set(key, mesh)
  }

  /**
   * Create a pointy-top hexagon shape
   */
  private createHexShape(radius: number): THREE.Shape {
    const shape = new THREE.Shape()
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 2
      const x = radius * Math.cos(angle)
      const y = radius * Math.sin(angle)
      if (i === 0) {
        shape.moveTo(x, y)
      } else {
        shape.lineTo(x, y)
      }
    }
    shape.closePath()
    return shape
  }

  /**
   * Spawn click pulse animation
   */
  spawnClickPulse(x: number, z: number): void {
    const clickedHex = this.hexGrid.cartesianToHex(x, z)

    // Expanding ring at click point
    const ringGeometry = new THREE.RingGeometry(0.2, 0.35, 32)
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0x8eefff,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
    const ring = new THREE.Mesh(ringGeometry, ringMaterial)
    ring.rotation.x = -Math.PI / 2
    ring.position.set(x, 0.03, z)
    this.scene.add(ring)

    this.clickPulses.push({
      mesh: ring,
      age: 0,
      maxAge: 0.5,
      type: 'ring',
    })

    // Hex wave ripple
    const spawnHexRing = (ringNum: number, strength: number) => {
      const hexes = ringNum === 0 ? [clickedHex] : this.hexGrid.getHexRing(clickedHex, ringNum)

      for (const hex of hexes) {
        // Skip if out of grid range
        if (Math.abs(hex.q) + Math.abs(hex.r) + Math.abs(-hex.q - hex.r) > this.gridRange * 2) continue

        const center = this.hexGrid.axialToCartesian(hex)
        const points: THREE.Vector3[] = []

        for (let i = 0; i <= 6; i++) {
          const angle = (Math.PI / 3) * i - Math.PI / 2
          points.push(new THREE.Vector3(
            center.x + this.hexGrid.hexRadius * Math.cos(angle),
            0.02,
            center.z + this.hexGrid.hexRadius * Math.sin(angle)
          ))
        }

        const geometry = new THREE.BufferGeometry().setFromPoints(points)
        const material = new THREE.LineBasicMaterial({
          color: 0x8eefff,
          transparent: true,
          opacity: strength,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        })
        const line = new THREE.Line(geometry, material)
        this.scene.add(line)

        this.clickPulses.push({
          mesh: line,
          age: 0,
          maxAge: 0.5,
          type: 'ripple',
          startOpacity: strength,
        })
      }
    }

    const maxRings = 5
    const msPerRing = 50

    for (let r = 0; r <= maxRings; r++) {
      const strength = Math.pow(0.6, r)
      if (strength < 0.05) continue
      if (r === 0) {
        spawnHexRing(0, strength)
      } else {
        setTimeout(() => spawnHexRing(r, strength), r * msPerRing)
      }
    }
  }

  /**
   * Update click pulses
   */
  private updateClickPulses(delta: number): void {
    for (let i = this.clickPulses.length - 1; i >= 0; i--) {
      const pulse = this.clickPulses[i]
      pulse.age += delta
      const progress = pulse.age / pulse.maxAge

      if (progress >= 1) {
        this.scene.remove(pulse.mesh)
        pulse.mesh.geometry.dispose()
        ;(pulse.mesh.material as THREE.Material).dispose()
        this.clickPulses.splice(i, 1)
      } else if (pulse.type === 'ring') {
        const scale = 1 + progress * 4
        pulse.mesh.scale.set(scale, scale, 1)
        ;(pulse.mesh.material as THREE.MeshBasicMaterial).opacity = 0.9 * (1 - progress * progress)
      } else if (pulse.type === 'ripple') {
        const fade = Math.pow(1 - progress, 2)
        ;(pulse.mesh.material as THREE.LineBasicMaterial).opacity = (pulse.startOpacity ?? 1) * fade
      }
    }
  }

  // ============================================================================
  // Procedural Pattern Functions
  // ============================================================================

  /**
   * Clear all painted hexes
   */
  clearAll(): void {
    this.hexData.clear()
    this.hexColors.clear()
    this.hexHeights.clear()
    for (const mesh of this.hexMeshes.values()) {
      this.hexMeshGroup.remove(mesh)
      mesh.geometry.dispose()
      ;(mesh.material as THREE.Material).dispose()
    }
    this.hexMeshes.clear()
  }

  /**
   * Set selected material by index
   */
  setMaterial(index: number): void {
    if (index >= 0 && index < MATERIALS.length) {
      this.selectedMaterial = MATERIALS[index]
      this.selectedColor = this.selectedMaterial.color
    }
  }

  // ============================================================================
  // Structure Generators (Trees, Rocks, Buildings)
  // ============================================================================

  /**
   * Generate a tree at position with trunk and canopy
   */
  private placeTree(center: HexCoord, style: 'oak' | 'pine' | 'palm' | 'birch' = 'oak'): void {
    const wood = getMaterial('wood')!
    const grass = getMaterial('grass')!
    const snow = getMaterial('snow')!

    switch (style) {
      case 'oak': {
        // Classic bushy tree
        const trunkHeight = 2 + Math.random() * 1.5
        this.paintHexWithMaterial(center, wood, trunkHeight, true)

        // Canopy - multiple layers
        for (const hex of this.hexGrid.getHexesInRadius(center, 2)) {
          const dist = Math.abs(hex.q - center.q) + Math.abs(hex.r - center.r)
          if (dist <= 2) {
            const leafHeight = trunkHeight + 0.8 + Math.random() * 0.5 - dist * 0.2
            this.paintHexWithMaterial(hex, grass, leafHeight, true)
          }
        }
        // Top leaves
        this.paintHexWithMaterial(center, grass, trunkHeight + 1.5, true)
        break
      }
      case 'pine': {
        // Tall conical pine tree
        const trunkHeight = 3 + Math.random() * 2
        this.paintHexWithMaterial(center, wood, trunkHeight, true)

        // Conical canopy layers
        for (let layer = 0; layer < 4; layer++) {
          const radius = 2 - Math.floor(layer * 0.5)
          const layerHeight = trunkHeight * 0.4 + layer * 0.8
          for (const hex of this.hexGrid.getHexesInRadius(center, radius)) {
            const dist = Math.abs(hex.q - center.q) + Math.abs(hex.r - center.r)
            if (dist <= radius) {
              this.paintHexWithMaterial(hex, grass, layerHeight + Math.random() * 0.3, true)
            }
          }
        }
        // Tip
        this.paintHexWithMaterial(center, grass, trunkHeight + 0.5, true)
        break
      }
      case 'palm': {
        // Tall trunk with fronds at top
        const trunkHeight = 3 + Math.random() * 2
        this.paintHexWithMaterial(center, wood, trunkHeight, true)

        // Palm fronds extending outward
        for (const dir of HEX_DIRECTIONS) {
          const frondHex = { q: center.q + dir.q, r: center.r + dir.r }
          this.paintHexWithMaterial(frondHex, grass, trunkHeight - 0.3, true)
          const farFrond = { q: center.q + dir.q * 2, r: center.r + dir.r * 2 }
          this.paintHexWithMaterial(farFrond, grass, trunkHeight - 0.8, true)
        }
        this.paintHexWithMaterial(center, grass, trunkHeight + 0.3, true)
        break
      }
      case 'birch': {
        // Slim white-ish trunk with delicate canopy
        const ice = getMaterial('ice')! // Use ice for white trunk
        const trunkHeight = 2.5 + Math.random() * 1.5
        this.paintHexWithMaterial(center, ice, trunkHeight, true)

        // Sparse canopy
        for (const hex of this.hexGrid.getHexesInRadius(center, 1)) {
          if (Math.random() < 0.7) {
            this.paintHexWithMaterial(hex, grass, trunkHeight + 0.5 + Math.random() * 0.3, true)
          }
        }
        break
      }
    }
  }

  /**
   * Generate a snowy tree (pine with snow)
   */
  private placeSnowyTree(center: HexCoord): void {
    const wood = getMaterial('wood')!
    const snow = getMaterial('snow')!
    const ice = getMaterial('ice')!

    const trunkHeight = 3 + Math.random() * 2
    this.paintHexWithMaterial(center, wood, trunkHeight, true)

    // Snowy pine layers
    for (let layer = 0; layer < 4; layer++) {
      const radius = 2 - Math.floor(layer * 0.5)
      const layerHeight = trunkHeight * 0.4 + layer * 0.8
      for (const hex of this.hexGrid.getHexesInRadius(center, radius)) {
        const dist = Math.abs(hex.q - center.q) + Math.abs(hex.r - center.r)
        if (dist <= radius) {
          const mat = Math.random() < 0.4 ? snow : ice
          this.paintHexWithMaterial(hex, mat, layerHeight + Math.random() * 0.3, true)
        }
      }
    }
    this.paintHexWithMaterial(center, snow, trunkHeight + 0.5, true)
  }

  /**
   * Generate a rock formation
   */
  private placeRocks(center: HexCoord, count: number = 3, maxHeight: number = 2): void {
    const stone = getMaterial('stone')!

    // Central rock
    this.paintHexWithMaterial(center, stone, maxHeight * (0.5 + Math.random() * 0.5), true)

    // Surrounding rocks
    const neighbors = this.hexGrid.getHexesInRadius(center, 2)
    for (let i = 0; i < count - 1 && i < neighbors.length; i++) {
      if (Math.random() < 0.6) {
        const height = maxHeight * (0.2 + Math.random() * 0.5)
        this.paintHexWithMaterial(neighbors[i], stone, height, true)
      }
    }
  }

  /**
   * Generate a small hut/house
   */
  private placeHouse(center: HexCoord, style: 'hut' | 'cottage' = 'hut'): void {
    const wood = getMaterial('wood')!
    const stone = getMaterial('stone')!

    if (style === 'hut') {
      // Simple wooden hut
      for (const hex of this.hexGrid.getHexesInRadius(center, 1)) {
        this.paintHexWithMaterial(hex, wood, 2, true)
      }
      // Peaked roof
      this.paintHexWithMaterial(center, wood, 3, true)
    } else {
      // Stone cottage with wooden roof
      for (const hex of this.hexGrid.getHexesInRadius(center, 1)) {
        this.paintHexWithMaterial(hex, stone, 2.5, true)
      }
      // Wooden roof
      for (const hex of this.hexGrid.getHexesInRadius(center, 1)) {
        this.paintHexWithMaterial(hex, wood, 3, true)
      }
      this.paintHexWithMaterial(center, wood, 3.5, true)
    }
  }

  /**
   * Generate a crystal cluster
   */
  private placeCrystal(center: HexCoord, material: HexMaterial, baseHeight: number = 3): void {
    // Main crystal
    this.paintHexWithMaterial(center, material, baseHeight, true)

    // Smaller crystals around
    for (const dir of HEX_DIRECTIONS) {
      if (Math.random() < 0.5) {
        const neighbor = { q: center.q + dir.q, r: center.r + dir.r }
        this.paintHexWithMaterial(neighbor, material, baseHeight * (0.3 + Math.random() * 0.4), true)
      }
    }
  }

  /**
   * Place a flower patch
   */
  private placeFlowers(center: HexCoord, radius: number = 2): void {
    const pink = getMaterial('pink')!
    const purple = getMaterial('purple')!
    const grass = getMaterial('grass')!

    for (const hex of this.hexGrid.getHexesInRadius(center, radius)) {
      if (Math.random() < 0.6) {
        const flower = Math.random() < 0.5 ? pink : purple
        this.paintHexWithMaterial(hex, flower, 0.3 + Math.random() * 0.2, true)
      } else {
        this.paintHexWithMaterial(hex, grass, 0.2, true)
      }
    }
  }

  // ============================================================================
  // Preset Worlds
  // ============================================================================

  /**
   * Load a preset world
   */
  loadWorld(worldName: string): void {
    this.clearAll()
    this._skyEnabled = true // Enable sky for preset worlds

    switch (worldName) {
      case 'volcano':
        this.worldVolcanoIsland()
        break
      case 'forest':
        this.worldForestFire()
        break
      case 'ice':
        this.worldIceCave()
        break
      case 'zen':
        this.worldZenGarden()
        break
      case 'chaos':
        this.worldElementChaos()
        break
      case 'canyon':
        this.worldWaterCanyon()
        break
      case 'night':
        this.worldStarryNight()
        break
      case 'beach':
        this.worldSunsetBeach()
        break
      case 'crystal':
        this.worldCrystalCavern()
        break
    }
  }

  /**
   * Starry Night - Beautiful at night time
   */
  private worldStarryNight(): void {
    const glass = getMaterial('glass')!
    const ice = getMaterial('ice')!
    const water = getMaterial('water')!
    const stone = getMaterial('stone')!
    const grass = getMaterial('grass')!

    // Set to night time
    this.setTimeOfDay(0)
    this.autoTime = false

    // Rolling hills
    for (let q = -20; q <= 20; q++) {
      for (let r = -20; r <= 20; r++) {
        if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) > 38) continue

        const noise = Math.sin(q * 0.3) * Math.cos(r * 0.3) * 0.5 + 0.5
        const height = 0.2 + noise * 0.4
        this.paintHexWithMaterial({ q, r }, grass, height, true)
      }
    }

    // Crystal towers (glass pillars that catch starlight)
    const towers = [
      { q: 0, r: 0, h: 5 },
      { q: -5, r: 2, h: 3 },
      { q: 4, r: -3, h: 4 },
      { q: -3, r: -4, h: 2.5 },
      { q: 6, r: 3, h: 3.5 },
      { q: -7, r: 5, h: 2 },
    ]

    for (const tower of towers) {
      // Base
      for (const hex of this.hexGrid.getHexesInRadius({ q: tower.q, r: tower.r }, 1)) {
        this.paintHexWithMaterial(hex, stone, 0.3, true)
      }
      // Crystal pillar
      this.paintHexWithMaterial({ q: tower.q, r: tower.r }, glass, tower.h, true)
    }

    // Reflecting pool
    for (const hex of this.hexGrid.getHexesInRadius({ q: 8, r: -8 }, 4)) {
      this.paintHexWithMaterial(hex, water, 0.2, true)
    }

    // Ice crystals around pool
    const icePositions = [
      { q: 5, r: -6 }, { q: 12, r: -7 }, { q: 10, r: -4 }, { q: 6, r: -11 }
    ]
    for (const pos of icePositions) {
      this.paintHexWithMaterial(pos, ice, 1 + Math.random(), true)
    }
  }

  /**
   * Sunset Beach - Golden hour tropical paradise
   */
  private worldSunsetBeach(): void {
    const sand = getMaterial('sand')!
    const water = getMaterial('water')!
    const wood = getMaterial('wood')!
    const grass = getMaterial('grass')!
    const stone = getMaterial('stone')!
    const cyan = getMaterial('cyan')!

    // Set to sunset
    this.setTimeOfDay(0.75)
    this.autoTime = false

    // Deep ocean with waves (left side)
    for (let q = -25; q <= -5; q++) {
      for (let r = -18; r <= 18; r++) {
        if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) > 40) continue
        const wave = Math.sin(q * 0.5 + r * 0.3) * 0.1
        const depth = Math.abs(q + 15) * 0.03 + 0.3 + wave
        this.paintHexWithMaterial({ q, r }, water, depth, true)
      }
    }

    // Shallow water / tide line
    for (let r = -18; r <= 18; r++) {
      if (Math.abs(r) > 16) continue
      const waveOffset = Math.sin(r * 0.4) * 0.5
      for (let q = -4; q <= -2; q++) {
        this.paintHexWithMaterial({ q: q + Math.floor(waveOffset), r }, water, 0.15, true)
      }
    }

    // Sandy beach with footprints
    for (let q = -3; q <= 6; q++) {
      for (let r = -18; r <= 18; r++) {
        if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) > 38) continue
        const height = 0.12 + (q + 3) * 0.02 + Math.random() * 0.02
        this.paintHexWithMaterial({ q, r }, sand, height, true)
      }
    }

    // Grassy dunes with palm trees
    for (let q = 7; q <= 18; q++) {
      for (let r = -18; r <= 18; r++) {
        if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) > 38) continue
        const noise = Math.sin(q * 0.4 + r * 0.3) * 0.3 + 0.4
        this.paintHexWithMaterial({ q, r }, grass, 0.15 + noise * 0.25, true)
      }
    }

    // Palm trees along beach edge
    const palmPositions = [
      { q: 4, r: -10 }, { q: 5, r: -6 }, { q: 6, r: -2 }, { q: 5, r: 3 },
      { q: 6, r: 7 }, { q: 4, r: 11 }, { q: 7, r: 0 }, { q: 8, r: -8 },
      { q: 9, r: 5 }, { q: 10, r: -3 }
    ]
    for (const pos of palmPositions) {
      this.placeTree(pos, 'palm')
    }

    // Wooden pier with railings
    for (let q = -10; q <= 3; q++) {
      this.paintHexWithMaterial({ q, r: 0 }, wood, 0.5, true)
      this.paintHexWithMaterial({ q, r: 1 }, wood, 0.5, true)
      // Railing posts
      if (q % 3 === 0) {
        this.paintHexWithMaterial({ q, r: -1 }, wood, 1.2, true)
        this.paintHexWithMaterial({ q, r: 2 }, wood, 1.2, true)
      }
    }

    // Pier supports going into water
    for (let q = -9; q <= 1; q += 3) {
      this.paintHexWithMaterial({ q, r: 0 }, wood, 1.0, true)
      this.paintHexWithMaterial({ q, r: 1 }, wood, 1.0, true)
    }

    // Beach hut
    this.placeHouse({ q: 12, r: -6 }, 'hut')

    // Rock formations and tide pools
    this.placeRocks({ q: 2, r: -12 }, 5, 1.0)
    this.placeRocks({ q: 3, r: 13 }, 4, 0.8)

    // Tide pool with colorful coral
    for (const hex of this.hexGrid.getHexesInRadius({ q: 0, r: -14 }, 2)) {
      if (Math.random() < 0.6) {
        this.paintHexWithMaterial(hex, water, 0.2, true)
      } else {
        this.paintHexWithMaterial(hex, Math.random() < 0.5 ? cyan : stone, 0.3, true)
      }
    }

    // Driftwood on beach
    this.paintHexWithMaterial({ q: 1, r: 5 }, wood, 0.3, true)
    this.paintHexWithMaterial({ q: 2, r: 5 }, wood, 0.25, true)
    this.paintHexWithMaterial({ q: 0, r: -7 }, wood, 0.35, true)

    // Flower patches in dunes
    this.placeFlowers({ q: 10, r: 10 }, 2)
    this.placeFlowers({ q: 14, r: -5 }, 2)
  }

  /**
   * Crystal Cavern - Underground crystal cave
   */
  private worldCrystalCavern(): void {
    const stone = getMaterial('stone')!
    const glass = getMaterial('glass')!
    const ice = getMaterial('ice')!
    const water = getMaterial('water')!
    const lava = getMaterial('lava')!

    // Set to night (underground feel)
    this.setTimeOfDay(0.1)
    this.autoTime = false

    // Cave floor
    for (let q = -15; q <= 15; q++) {
      for (let r = -15; r <= 15; r++) {
        if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) > 28) continue
        this.paintHexWithMaterial({ q, r }, stone, 0.2, true)
      }
    }

    // Cave walls (ring around edge)
    for (let ring = 12; ring <= 14; ring++) {
      const hexes = this.hexGrid.getHexRing({ q: 0, r: 0 }, ring)
      for (const hex of hexes) {
        const height = 3 + Math.random() * 2
        this.paintHexWithMaterial(hex, stone, height, true)
      }
    }

    // Giant crystals
    const crystals = [
      { q: -5, r: 2, mat: glass, h: 4 },
      { q: 3, r: -4, mat: glass, h: 3.5 },
      { q: 6, r: 2, mat: ice, h: 3 },
      { q: -2, r: 6, mat: ice, h: 2.5 },
      { q: -7, r: -3, mat: glass, h: 5 },
      { q: 4, r: 5, mat: ice, h: 2 },
      { q: 0, r: -6, mat: glass, h: 4.5 },
    ]

    for (const crystal of crystals) {
      this.paintHexWithMaterial({ q: crystal.q, r: crystal.r }, crystal.mat, crystal.h, true)
      // Smaller crystals around
      for (const dir of HEX_DIRECTIONS.slice(0, 3)) {
        const neighbor = { q: crystal.q + dir.q, r: crystal.r + dir.r }
        if (Math.random() < 0.5) {
          this.paintHexWithMaterial(neighbor, crystal.mat, crystal.h * 0.4, true)
        }
      }
    }

    // Underground lake
    for (const hex of this.hexGrid.getHexesInRadius({ q: 0, r: 0 }, 3)) {
      this.paintHexWithMaterial(hex, water, 0.4, true)
    }

    // Lava vein (provides light)
    const lavaPath = [
      { q: -10, r: 8 }, { q: -9, r: 7 }, { q: -8, r: 7 },
      { q: -7, r: 6 }, { q: -6, r: 6 }, { q: -5, r: 6 }
    ]
    for (const pos of lavaPath) {
      this.paintHexWithMaterial(pos, lava, 0.3, true)
    }

    // Stalagmites
    for (let i = 0; i < 15; i++) {
      const q = Math.floor(Math.random() * 20) - 10
      const r = Math.floor(Math.random() * 20) - 10
      if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) <= 22 &&
          Math.abs(q) + Math.abs(r) + Math.abs(-q - r) > 8) {
        this.paintHexWithMaterial({ q, r }, stone, 0.5 + Math.random() * 1.5, true)
      }
    }
  }

  /**
   * Volcano Island - Dramatic volcanic island with lava flows
   */
  private worldVolcanoIsland(): void {
    const stone = getMaterial('stone')!
    const lava = getMaterial('lava')!
    const water = getMaterial('water')!
    const earth = getMaterial('earth')!
    const grass = getMaterial('grass')!
    const fire = getMaterial('fire')!
    const sand = getMaterial('sand')!

    // Surrounding ocean
    for (let q = -20; q <= 20; q++) {
      for (let r = -20; r <= 20; r++) {
        if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) > 38) continue
        const dist = Math.sqrt(q * q + r * r)
        if (dist > 10) {
          const depth = 0.3 + Math.sin(q * 0.3) * 0.1 + Math.random() * 0.05
          this.paintHexWithMaterial({ q, r }, water, depth, true)
        }
      }
    }

    // Beach ring around island
    for (const hex of this.hexGrid.getHexRing({ q: 0, r: 0 }, 10)) {
      this.paintHexWithMaterial(hex, sand, 0.2, true)
    }
    for (const hex of this.hexGrid.getHexRing({ q: 0, r: 0 }, 9)) {
      this.paintHexWithMaterial(hex, sand, 0.25, true)
    }

    // Grassy slopes
    for (let ring = 7; ring <= 8; ring++) {
      for (const hex of this.hexGrid.getHexRing({ q: 0, r: 0 }, ring)) {
        const height = 0.3 + Math.random() * 0.2
        this.paintHexWithMaterial(hex, grass, height, true)
      }
    }

    // Rocky volcano slopes with height
    for (let ring = 3; ring <= 6; ring++) {
      for (const hex of this.hexGrid.getHexRing({ q: 0, r: 0 }, ring)) {
        const height = (7 - ring) * 0.8 + Math.random() * 0.3
        this.paintHexWithMaterial(hex, stone, height, true)
      }
    }

    // Crater rim (highest point)
    for (const hex of this.hexGrid.getHexRing({ q: 0, r: 0 }, 2)) {
      this.paintHexWithMaterial(hex, stone, 4 + Math.random() * 0.5, true)
    }

    // Lava crater
    for (const hex of this.hexGrid.getHexesInRadius({ q: 0, r: 0 }, 1)) {
      this.paintHexWithMaterial(hex, lava, 3.5, true)
    }
    this.paintHexWithMaterial({ q: 0, r: 0 }, lava, 3.8, true)

    // Lava flows down the sides
    const lavaFlows = [
      [{ q: 2, r: -1 }, { q: 3, r: -1 }, { q: 4, r: -2 }, { q: 5, r: -2 }, { q: 6, r: -3 }],
      [{ q: -1, r: 2 }, { q: -2, r: 3 }, { q: -3, r: 4 }, { q: -4, r: 5 }],
    ]
    for (const flow of lavaFlows) {
      for (let i = 0; i < flow.length; i++) {
        const height = 3.5 - i * 0.5
        this.paintHexWithMaterial(flow[i], lava, height, true)
      }
    }

    // Smoke/fire at crater rim
    const smokePositions = [{ q: 1, r: 1 }, { q: -1, r: 2 }, { q: 2, r: -1 }]
    for (const pos of smokePositions) {
      this.paintHexWithMaterial(pos, fire, 4.5, true)
    }

    // Palm trees on the green slopes
    const treePositions = [
      { q: 7, r: -3 }, { q: -5, r: 8 }, { q: 8, r: 0 }, { q: -7, r: 3 },
      { q: 5, r: 4 }, { q: -3, r: 7 }, { q: 6, r: -6 }, { q: -8, r: -1 }
    ]
    for (const pos of treePositions) {
      this.placeTree(pos, 'palm')
    }

    // Small islands with vegetation
    const islands = [
      { center: { q: 15, r: -5 }, radius: 2 },
      { center: { q: -12, r: 10 }, radius: 3 },
      { center: { q: -8, r: -12 }, radius: 2 },
    ]
    for (const island of islands) {
      for (const hex of this.hexGrid.getHexesInRadius(island.center, island.radius)) {
        const dist = Math.abs(hex.q - island.center.q) + Math.abs(hex.r - island.center.r)
        if (dist === 0) {
          this.paintHexWithMaterial(hex, grass, 0.4, true)
          this.placeTree(hex, 'palm')
        } else if (dist <= 1) {
          this.paintHexWithMaterial(hex, grass, 0.3, true)
        } else {
          this.paintHexWithMaterial(hex, sand, 0.2, true)
        }
      }
    }

    // Rock formations in water
    this.placeRocks({ q: 12, r: 6 }, 3, 1.5)
    this.placeRocks({ q: -14, r: -4 }, 2, 1.2)
  }

  /**
   * Forest Fire - Dense forest with spreading fire
   */
  private worldForestFire(): void {
    const grass = getMaterial('grass')!
    const earth = getMaterial('earth')!
    const fire = getMaterial('fire')!
    const water = getMaterial('water')!
    const stone = getMaterial('stone')!

    // Grass field with height variation
    for (let q = -20; q <= 20; q++) {
      for (let r = -20; r <= 20; r++) {
        if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) > 38) continue
        const noise = Math.sin(q * 0.2) * Math.cos(r * 0.2) * 0.15
        this.paintHexWithMaterial({ q, r }, grass, 0.15 + noise, true)
      }
    }

    // Dense forest with mixed tree types
    const usedPositions = new Set<string>()

    // Place oak trees (scattered across forest)
    for (let i = 0; i < 25; i++) {
      const q = Math.floor(Math.random() * 30) - 15
      const r = Math.floor(Math.random() * 30) - 15
      const key = `${q},${r}`
      if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) <= 32 && !usedPositions.has(key)) {
        // Leave space around trees
        for (let dq = -3; dq <= 3; dq++) {
          for (let dr = -3; dr <= 3; dr++) {
            usedPositions.add(`${q + dq},${r + dr}`)
          }
        }
        this.placeTree({ q, r }, 'oak')
      }
    }

    // Place pine trees (more in one area)
    for (let i = 0; i < 15; i++) {
      const q = Math.floor(Math.random() * 15) + 5
      const r = Math.floor(Math.random() * 20) - 10
      const key = `${q},${r}`
      if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) <= 32 && !usedPositions.has(key)) {
        for (let dq = -3; dq <= 3; dq++) {
          for (let dr = -3; dr <= 3; dr++) {
            usedPositions.add(`${q + dq},${r + dr}`)
          }
        }
        this.placeTree({ q, r }, 'pine')
      }
    }

    // Birch grove
    for (let i = 0; i < 8; i++) {
      const q = Math.floor(Math.random() * 10) - 15
      const r = Math.floor(Math.random() * 10) - 5
      const key = `${q},${r}`
      if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) <= 32 && !usedPositions.has(key)) {
        for (let dq = -2; dq <= 2; dq++) {
          for (let dr = -2; dr <= 2; dr++) {
            usedPositions.add(`${q + dq},${r + dr}`)
          }
        }
        this.placeTree({ q, r }, 'birch')
      }
    }

    // Dirt path through forest
    for (let i = -18; i <= 18; i++) {
      const offset = Math.floor(Math.sin(i * 0.3) * 2)
      this.paintHexWithMaterial({ q: i, r: offset }, earth, 0.12, true)
      this.paintHexWithMaterial({ q: i, r: offset + 1 }, earth, 0.12, true)
    }

    // Small stream
    for (let r = -15; r <= 5; r++) {
      const q = -8 + Math.floor(Math.sin(r * 0.4) * 2)
      this.paintHexWithMaterial({ q, r }, water, 0.3, true)
    }

    // Rock formations
    this.placeRocks({ q: 10, r: -12 }, 4, 1.5)
    this.placeRocks({ q: -5, r: 12 }, 3, 1.2)

    // Wooden cabin in a clearing
    this.placeHouse({ q: 0, r: 8 }, 'hut')

    // Flower patches
    this.placeFlowers({ q: -12, r: -10 }, 2)
    this.placeFlowers({ q: 8, r: 10 }, 2)

    // Fire spreading from edge (dramatic!)
    const fireStarts = [
      { q: -18, r: 0 }, { q: -17, r: 1 }, { q: -17, r: -1 },
      { q: -16, r: 0 }, { q: -16, r: 2 }, { q: -18, r: 2 }
    ]
    for (const pos of fireStarts) {
      this.paintHexWithMaterial(pos, fire, 0.6 + Math.random() * 0.4, true)
    }
  }

  /**
   * Ice Cave - Snowy tundra with frozen cave entrance
   */
  private worldIceCave(): void {
    const ice = getMaterial('ice')!
    const snow = getMaterial('snow')!
    const stone = getMaterial('stone')!
    const lava = getMaterial('lava')!
    const water = getMaterial('water')!
    const glass = getMaterial('glass')!

    // Set to night for aurora effect
    this.setTimeOfDay(0.05)
    this.autoTime = false

    // Snowy ground with hills
    for (let q = -18; q <= 18; q++) {
      for (let r = -18; r <= 18; r++) {
        if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) > 34) continue
        const noise = Math.sin(q * 0.2) * Math.cos(r * 0.25) * 0.2 + 0.15
        this.paintHexWithMaterial({ q, r }, snow, noise + Math.random() * 0.05, true)
      }
    }

    // Frozen lake
    for (const hex of this.hexGrid.getHexesInRadius({ q: -8, r: 4 }, 5)) {
      this.paintHexWithMaterial(hex, ice, 0.1, true)
    }

    // Cave entrance (stone mountain with opening)
    for (let ring = 0; ring <= 6; ring++) {
      for (const hex of this.hexGrid.getHexRing({ q: 8, r: -6 }, ring)) {
        if (Math.abs(hex.q) + Math.abs(hex.r) + Math.abs(-hex.q - hex.r) > 34) continue
        const height = Math.max(0.3, (6 - ring) * 0.7 + Math.random() * 0.3)
        this.paintHexWithMaterial(hex, stone, height, true)
      }
    }
    // Cave opening
    for (const hex of this.hexGrid.getHexesInRadius({ q: 5, r: -5 }, 1)) {
      this.paintHexWithMaterial(hex, stone, 0.5, true)
    }

    // Snowy pine trees scattered around
    const treePositions = new Set<string>()
    for (let i = 0; i < 20; i++) {
      const q = Math.floor(Math.random() * 30) - 15
      const r = Math.floor(Math.random() * 30) - 15
      const key = `${q},${r}`
      if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) <= 30 &&
          !treePositions.has(key) &&
          Math.sqrt((q + 8) ** 2 + (r - 4) ** 2) > 6 && // Away from lake
          Math.sqrt((q - 8) ** 2 + (r + 6) ** 2) > 7) { // Away from cave
        for (let dq = -3; dq <= 3; dq++) {
          for (let dr = -3; dr <= 3; dr++) {
            treePositions.add(`${q + dq},${r + dr}`)
          }
        }
        this.placeSnowyTree({ q, r })
      }
    }

    // Ice crystals/pillars
    this.placeCrystal({ q: -12, r: -8 }, ice, 3)
    this.placeCrystal({ q: 0, r: 10 }, ice, 2.5)
    this.placeCrystal({ q: -5, r: -10 }, glass, 4)

    // Lava crack near cave (underground heat)
    const lavaCrack = [
      { q: 12, r: -4 }, { q: 13, r: -5 }, { q: 13, r: -4 }, { q: 14, r: -5 }
    ]
    for (const pos of lavaCrack) {
      this.paintHexWithMaterial(pos, lava, 0.4, true)
    }

    // Melting ice near lava
    for (const pos of [{ q: 11, r: -3 }, { q: 11, r: -4 }]) {
      this.paintHexWithMaterial(pos, water, 0.2, true)
    }

    // Snow drifts (larger mounds)
    for (const center of [{ q: 4, r: 8 }, { q: -14, r: 2 }, { q: 10, r: 6 }]) {
      for (const hex of this.hexGrid.getHexesInRadius(center, 2)) {
        const dist = Math.abs(hex.q - center.q) + Math.abs(hex.r - center.r)
        this.paintHexWithMaterial(hex, snow, 0.6 - dist * 0.15, true)
      }
    }

    // Ice spikes
    const spikes = [{ q: -10, r: 0 }, { q: 3, r: -12 }, { q: -3, r: 12 }]
    for (const pos of spikes) {
      this.paintHexWithMaterial(pos, ice, 2 + Math.random(), true)
    }
  }

  /**
   * Zen Garden - Peaceful water and stone
   */
  private worldZenGarden(): void {
    const sand = getMaterial('sand')!
    const stone = getMaterial('stone')!
    const water = getMaterial('water')!
    const grass = getMaterial('grass')!
    const wood = getMaterial('wood')!

    // Sand base (raked pattern)
    for (let q = -15; q <= 15; q++) {
      for (let r = -15; r <= 15; r++) {
        if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) > 28) continue
        this.paintHexWithMaterial({ q, r }, sand, 0.15, true)
      }
    }

    // Central pond
    for (const hex of this.hexGrid.getHexesInRadius({ q: 0, r: 0 }, 4)) {
      this.paintHexWithMaterial(hex, water, 0.5, true)
    }

    // Stepping stones across pond
    const steps = [{ q: -3, r: 1 }, { q: -1, r: 0 }, { q: 1, r: 0 }, { q: 3, r: -1 }]
    for (const pos of steps) {
      this.paintHexWithMaterial(pos, stone, 0.6, true)
    }

    // Rock formations
    const rockGroups = [
      { center: { q: -8, r: 2 }, count: 5 },
      { center: { q: 7, r: -4 }, count: 3 },
      { center: { q: 3, r: 8 }, count: 4 },
    ]

    for (const group of rockGroups) {
      const hexes = this.hexGrid.getHexesInRadius(group.center, 1)
      for (let i = 0; i < Math.min(group.count, hexes.length); i++) {
        const height = 0.5 + Math.random() * 1.5
        this.paintHexWithMaterial(hexes[i], stone, height, true)
      }
    }

    // Grass patches
    const grassPatches = [{ q: -10, r: -5 }, { q: 10, r: 5 }, { q: -5, r: 10 }, { q: 5, r: -10 }]
    for (const center of grassPatches) {
      for (const hex of this.hexGrid.getHexesInRadius(center, 2)) {
        if (Math.random() < 0.7) {
          this.paintHexWithMaterial(hex, grass, 0.25, true)
        }
      }
    }

    // Wooden bridge
    for (let i = -6; i <= 6; i++) {
      this.paintHexWithMaterial({ q: i, r: 6 }, wood, 0.4, true)
    }

    // Bamboo (tall wood)
    const bamboo = [{ q: -12, r: 4 }, { q: -11, r: 4 }, { q: -12, r: 5 }, { q: 11, r: -3 }, { q: 12, r: -4 }]
    for (const pos of bamboo) {
      this.paintHexWithMaterial(pos, wood, 2 + Math.random(), true)
    }
  }

  /**
   * Element Chaos - Everything interacting!
   */
  private worldElementChaos(): void {
    const water = getMaterial('water')!
    const lava = getMaterial('lava')!
    const fire = getMaterial('fire')!
    const ice = getMaterial('ice')!
    const sand = getMaterial('sand')!
    const grass = getMaterial('grass')!
    const wood = getMaterial('wood')!
    const snow = getMaterial('snow')!
    const stone = getMaterial('stone')!

    // Quadrants with different elements

    // Top-left: Ice & Snow
    for (let q = -12; q <= -2; q++) {
      for (let r = -12; r <= -2; r++) {
        if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) > 24) continue
        const mat = Math.random() < 0.6 ? ice : snow
        this.paintHexWithMaterial({ q, r }, mat, 0.3 + Math.random() * 0.5, true)
      }
    }

    // Top-right: Lava lake
    for (let q = 2; q <= 12; q++) {
      for (let r = -12; r <= -2; r++) {
        if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) > 24) continue
        this.paintHexWithMaterial({ q, r }, lava, 0.4 + Math.random() * 0.3, true)
      }
    }

    // Bottom-left: Forest
    for (let q = -12; q <= -2; q++) {
      for (let r = 2; r <= 12; r++) {
        if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) > 24) continue
        const mat = Math.random() < 0.3 ? wood : grass
        const height = mat === wood ? 0.8 + Math.random() * 1.5 : 0.2
        this.paintHexWithMaterial({ q, r }, mat, height, true)
      }
    }

    // Bottom-right: Water
    for (let q = 2; q <= 12; q++) {
      for (let r = 2; r <= 12; r++) {
        if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) > 24) continue
        this.paintHexWithMaterial({ q, r }, water, 0.5, true)
      }
    }

    // Center: Mix zone with stone platform
    for (const hex of this.hexGrid.getHexesInRadius({ q: 0, r: 0 }, 3)) {
      this.paintHexWithMaterial(hex, stone, 0.8, true)
    }

    // Sand barriers between zones
    for (let i = -12; i <= 12; i++) {
      this.paintHexWithMaterial({ q: i, r: 0 }, sand, 1.5, true)
      this.paintHexWithMaterial({ q: 0, r: i }, sand, 1.5, true)
    }

    // Start some fires near the forest edge
    this.paintHexWithMaterial({ q: -2, r: 3 }, fire, 0.5, true)
    this.paintHexWithMaterial({ q: -2, r: 4 }, fire, 0.5, true)
  }

  /**
   * Water Canyon - Waterfall through canyon
   */
  private worldWaterCanyon(): void {
    const stone = getMaterial('stone')!
    const water = getMaterial('water')!
    const earth = getMaterial('earth')!
    const grass = getMaterial('grass')!
    const sand = getMaterial('sand')!

    // Canyon walls
    for (let r = -15; r <= 15; r++) {
      // Left wall
      for (let q = -10; q <= -4; q++) {
        const height = 2 + Math.abs(q + 7) * 0.5 + Math.random() * 0.5
        this.paintHexWithMaterial({ q, r }, stone, height, true)
      }
      // Right wall
      for (let q = 4; q <= 10; q++) {
        const height = 2 + Math.abs(q - 7) * 0.5 + Math.random() * 0.5
        this.paintHexWithMaterial({ q, r }, stone, height, true)
      }
    }

    // Canyon floor
    for (let r = -15; r <= 15; r++) {
      for (let q = -3; q <= 3; q++) {
        this.paintHexWithMaterial({ q, r }, sand, 0.2, true)
      }
    }

    // River through canyon
    for (let r = -15; r <= 5; r++) {
      for (let q = -1; q <= 1; q++) {
        this.paintHexWithMaterial({ q, r }, water, 0.6, true)
      }
    }

    // Water source at top (will flow down)
    for (const hex of this.hexGrid.getHexesInRadius({ q: 0, r: -15 }, 2)) {
      this.paintHexWithMaterial(hex, water, 1.5, true)
    }

    // Pool at bottom
    for (const hex of this.hexGrid.getHexesInRadius({ q: 0, r: 12 }, 4)) {
      this.paintHexWithMaterial(hex, water, 0.4, true)
    }

    // Grass on top of canyon
    for (let r = -15; r <= 15; r++) {
      this.paintHexWithMaterial({ q: -11, r }, grass, 0.3, true)
      this.paintHexWithMaterial({ q: 11, r }, grass, 0.3, true)
    }

    // Bridge across canyon
    for (let q = -5; q <= 5; q++) {
      this.paintHexWithMaterial({ q, r: 0 }, stone, 2.5, true)
    }
  }

  /**
   * Toggle 3D extrusion mode
   */
  toggle3DMode(): boolean {
    this.is3DMode = !this.is3DMode
    return this.is3DMode
  }

  /**
   * Fill with rainbow spiral pattern
   */
  patternRainbowSpiral(): void {
    const colors = [0xef4444, 0xfbbf24, 0x4ade80, 0x22d3d8, 0x60a5fa, 0xa78bfa, 0xf472b6]
    let index = 0

    for (let ring = 0; ring <= this.gridRange; ring++) {
      const hexes = this.hexGrid.getHexRing({ q: 0, r: 0 }, ring)
      for (const hex of hexes) {
        if (Math.abs(hex.q) + Math.abs(hex.r) + Math.abs(-hex.q - hex.r) <= this.gridRange * 2) {
          const color = colors[index % colors.length]
          this.paintHex(hex, color, true)
          index++
        }
      }
    }
  }

  /**
   * Fill with concentric ring pattern
   */
  patternConcentricRings(): void {
    const colors = [0x4ac8e8, 0x1a1a2e]

    for (let ring = 0; ring <= this.gridRange; ring++) {
      const hexes = this.hexGrid.getHexRing({ q: 0, r: 0 }, ring)
      const color = colors[ring % 2]
      for (const hex of hexes) {
        if (Math.abs(hex.q) + Math.abs(hex.r) + Math.abs(-hex.q - hex.r) <= this.gridRange * 2) {
          if (color !== 0x1a1a2e) {
            this.paintHex(hex, color, true)
          }
        }
      }
    }
  }

  /**
   * Random color fill
   */
  patternRandomFill(): void {
    const colors = PALETTE.slice(0, -1) // Exclude erase color

    for (let q = -this.gridRange; q <= this.gridRange; q++) {
      for (let r = -this.gridRange; r <= this.gridRange; r++) {
        if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) <= this.gridRange * 2) {
          const color = colors[Math.floor(Math.random() * colors.length)]
          this.paintHex({ q, r }, color, true)
        }
      }
    }
  }

  /**
   * Gradient from center
   */
  patternGradient(): void {
    const centerColor = new THREE.Color(0x4ac8e8)
    const edgeColor = new THREE.Color(0xa78bfa)

    for (let q = -this.gridRange; q <= this.gridRange; q++) {
      for (let r = -this.gridRange; r <= this.gridRange; r++) {
        const dist = (Math.abs(q) + Math.abs(r) + Math.abs(-q - r)) / 2
        if (dist <= this.gridRange) {
          const t = dist / this.gridRange
          const color = new THREE.Color().lerpColors(centerColor, edgeColor, t)
          this.paintHex({ q, r }, color.getHex(), true)
        }
      }
    }
  }

  /**
   * Checkerboard pattern
   */
  patternCheckerboard(): void {
    const colors = [0x4ac8e8, 0xf472b6]

    for (let q = -this.gridRange; q <= this.gridRange; q++) {
      for (let r = -this.gridRange; r <= this.gridRange; r++) {
        if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) <= this.gridRange * 2) {
          // Use cube coordinate sum mod 3 for hex checkerboard
          const s = -q - r
          const idx = ((q % 3) + 3) % 3
          if (idx !== 0) {
            this.paintHex({ q, r }, colors[idx - 1], true)
          }
        }
      }
    }
  }

  /**
   * Flower pattern
   */
  patternFlower(): void {
    // Center
    this.paintHex({ q: 0, r: 0 }, 0xfbbf24, true)

    // Inner petals
    for (const dir of HEX_DIRECTIONS) {
      this.paintHex(dir, 0xf472b6, true)
    }

    // Outer petals
    for (let i = 0; i < 6; i++) {
      const dir = HEX_DIRECTIONS[i]
      const nextDir = HEX_DIRECTIONS[(i + 1) % 6]
      this.paintHex({ q: dir.q * 2, r: dir.r * 2 }, 0xa78bfa, true)
      this.paintHex({ q: dir.q + nextDir.q, r: dir.r + nextDir.r }, 0x60a5fa, true)
    }
  }

  // ============================================================================
  // Game of Life
  // ============================================================================

  /**
   * Count alive (painted) neighbors for a hex
   */
  private countNeighbors(hex: HexCoord): number {
    let count = 0
    for (const dir of HEX_DIRECTIONS) {
      const neighbor = { q: hex.q + dir.q, r: hex.r + dir.r }
      const key = this.hexGrid.hexKey(neighbor)
      if (this.hexColors.has(key)) {
        count++
      }
    }
    return count
  }

  /**
   * Run one generation of Game of Life
   * Hex rules (6 neighbors):
   * - Dead cell with exactly 2 neighbors → becomes alive
   * - Alive cell with 2-3 neighbors → survives
   * - Otherwise → dies
   */
  golStep(): void {
    const births: HexCoord[] = []
    const deaths: HexCoord[] = []
    const checked = new Set<string>()

    // Check all alive cells and their neighbors
    for (const [key] of this.hexColors) {
      const [q, r] = key.split(',').map(Number)
      const hex = { q, r }

      // Check this cell
      if (!checked.has(key)) {
        checked.add(key)
        const neighbors = this.countNeighbors(hex)
        if (neighbors < 2 || neighbors > 3) {
          deaths.push(hex)
        }
      }

      // Check all neighbors (they might be born)
      for (const dir of HEX_DIRECTIONS) {
        const neighbor = { q: hex.q + dir.q, r: hex.r + dir.r }
        const neighborKey = this.hexGrid.hexKey(neighbor)

        if (checked.has(neighborKey)) continue
        checked.add(neighborKey)

        // Skip if out of range
        if (Math.abs(neighbor.q) + Math.abs(neighbor.r) + Math.abs(-neighbor.q - neighbor.r) > this.gridRange * 2) continue

        // If dead, check for birth
        if (!this.hexColors.has(neighborKey)) {
          const neighborCount = this.countNeighbors(neighbor)
          if (neighborCount === 2) {
            births.push(neighbor)
          }
        }
      }
    }

    // Apply changes
    for (const hex of deaths) {
      const key = this.hexGrid.hexKey(hex)
      this.hexColors.delete(key)
      const mesh = this.hexMeshes.get(key)
      if (mesh) {
        this.hexMeshGroup.remove(mesh)
        mesh.geometry.dispose()
        ;(mesh.material as THREE.Material).dispose()
        this.hexMeshes.delete(key)
      }
    }

    for (const hex of births) {
      this.paintHex(hex, this.selectedColor, true)
    }
  }

  /**
   * Toggle auto-running Game of Life
   */
  golToggleRun(): boolean {
    if (this.golRunning) {
      this.golStop()
      return false
    } else {
      this.golStart()
      return true
    }
  }

  /**
   * Start auto-running Game of Life
   */
  golStart(): void {
    if (this.golRunning) return
    this.golRunning = true
    this.golIntervalId = window.setInterval(() => {
      this.golStep()
    }, 150)
  }

  /**
   * Stop auto-running Game of Life
   */
  golStop(): void {
    this.golRunning = false
    if (this.golIntervalId !== null) {
      clearInterval(this.golIntervalId)
      this.golIntervalId = null
    }
  }

  /**
   * Check if Game of Life is running
   */
  isGolRunning(): boolean {
    return this.golRunning
  }

  // ============================================================================
  // Gravity Mode
  // ============================================================================

  /**
   * Run one gravity step - move hexes "down" (+r direction)
   * Returns true if any hex moved
   */
  gravityStep(): boolean {
    // Sort hexes by r coordinate (highest r first, so they fall first)
    const hexList: { hex: HexCoord; color: number }[] = []
    for (const [key, color] of this.hexColors) {
      const [q, r] = key.split(',').map(Number)
      hexList.push({ hex: { q, r }, color })
    }
    hexList.sort((a, b) => b.hex.r - a.hex.r)

    let anyMoved = false

    for (const { hex, color } of hexList) {
      // Try to move down (in hex coords, "down" is +r direction)
      const below = { q: hex.q, r: hex.r + 1 }
      const belowKey = this.hexGrid.hexKey(below)

      // Check if can move down
      const inRange = Math.abs(below.q) + Math.abs(below.r) + Math.abs(-below.q - below.r) <= this.gridRange * 2
      const occupied = this.hexColors.has(belowKey)

      if (inRange && !occupied) {
        // Move hex down
        const oldKey = this.hexGrid.hexKey(hex)

        // Remove from old position
        this.hexColors.delete(oldKey)
        const mesh = this.hexMeshes.get(oldKey)
        if (mesh) {
          this.hexMeshGroup.remove(mesh)
          mesh.geometry.dispose()
          ;(mesh.material as THREE.Material).dispose()
          this.hexMeshes.delete(oldKey)
        }

        // Add at new position
        this.hexColors.set(belowKey, color)
        this.paintHex(below, color, true)

        anyMoved = true
      }
    }

    return anyMoved
  }

  /**
   * Toggle gravity mode
   */
  gravityToggle(): boolean {
    if (this.gravityRunning) {
      this.gravityStop()
      return false
    } else {
      this.gravityStart()
      return true
    }
  }

  /**
   * Start gravity mode
   */
  gravityStart(): void {
    if (this.gravityRunning) return
    this.gravityRunning = true
    this.gravityIntervalId = window.setInterval(() => {
      const moved = this.gravityStep()
      if (!moved) {
        // Nothing moved, stop gravity
        this.gravityStop()
      }
    }, 50)
  }

  /**
   * Stop gravity mode
   */
  gravityStop(): void {
    this.gravityRunning = false
    if (this.gravityIntervalId !== null) {
      clearInterval(this.gravityIntervalId)
      this.gravityIntervalId = null
    }
  }

  /**
   * Check if gravity is running
   */
  isGravityRunning(): boolean {
    return this.gravityRunning
  }

  // ============================================================================
  // Animation Loop
  // ============================================================================

  private handleResize = (): void => {
    const width = this.container.clientWidth
    const height = this.container.clientHeight

    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(width, height)
  }

  start(): void {
    if (this.animationId !== null) return

    const animate = () => {
      this.animationId = requestAnimationFrame(animate)

      const delta = this.clock.getDelta()
      this.time += delta

      // Update controls based on mode
      if (this.isFPSMode) {
        this.updateFPSMovement(delta)
      } else {
        this.controls.update()
      }

      this.updateClickPulses(delta)
      this.updateAnimatedMaterials(delta)
      this.updateParticles(delta)
      this.updateDayNightCycle(delta)

      this.renderer.render(this.scene, this.camera)
    }

    animate()
  }

  /**
   * Update animated materials (water, lava, fire shimmer)
   */
  private updateAnimatedMaterials(delta: number): void {
    for (const [key, data] of this.hexData) {
      if (!data.material.animated) continue

      const mesh = this.hexMeshes.get(key)
      if (!mesh) continue

      const mat = mesh.material as THREE.MeshStandardMaterial
      const [q, r] = key.split(',').map(Number)

      // Pulsing for lava
      if (data.material.id === 'lava') {
        const pulse = 0.7 + 0.3 * Math.sin(this.time * 3 + q * 0.5 + r * 0.3)
        mat.emissiveIntensity = data.material.emissiveIntensity * pulse
      }

      // Flickering for fire
      if (data.material.id === 'fire') {
        const flicker = 0.6 + 0.4 * Math.sin(this.time * 8 + q * 1.2 + r * 0.8) * Math.cos(this.time * 5 + r)
        mat.emissiveIntensity = data.material.emissiveIntensity * flicker
        // Also vary the color slightly
        const colorShift = 0.9 + 0.1 * Math.sin(this.time * 6 + q)
        mat.emissive.setHex(Math.floor(0xff4500 * colorShift))
      }

      // Wave for water
      if (data.material.id === 'water') {
        const wave = 0.05 + 0.03 * Math.sin(this.time * 2 + r * 0.3 + q * 0.2)
        mesh.position.y = 0.02 + wave
      }

      // Steam wobble
      if (data.material.id === 'steam') {
        const wobble = 0.02 * Math.sin(this.time * 4 + q + r)
        mesh.position.x += wobble * delta
        mat.opacity = 0.3 + 0.1 * Math.sin(this.time * 2 + r)
      }
    }
  }

  stop(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
  }

  dispose(): void {
    this.stop()
    window.removeEventListener('resize', this.handleResize)
    this.renderer.domElement.removeEventListener('mousemove', this.handleHover)
    this.renderer.domElement.removeEventListener('mouseleave', this.handleHoverLeave)
    this.renderer.dispose()
  }
}
